import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import axios from '../../api/axiosConfig';
import { showError, showSuccess } from '../../utils/toast';
import { isAllowedImageFile } from '../../utils/imageUpload';
import AutoResizeTextarea from './AutoResizeTextarea';
import { servicesAPI } from '../../api/services';
import './ServiceForm.css';

// API base for uploads - use full URL so /api/uploads path works on cPanel
import { getApiBaseUrl } from '../../api/apiConfig';

/** Convert clipboard data URL to File for /upload-image */
function dataUrlToFile(dataurl, filename) {
  const arr = dataurl.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) u8arr[n] = bstr.charCodeAt(n);
  return new File([u8arr], filename, { type: mime });
}

function toolIconLooksLikeUrl(s) {
  if (!s || typeof s !== 'string') return false;
  const t = s.trim();
  return t.startsWith('http://') || t.startsWith('https://') || t.startsWith('/') || t.startsWith('data:');
}

function toolIconPreviewSrc(icon) {
  if (!toolIconLooksLikeUrl(icon)) return null;
  const t = icon.trim();
  if (t.startsWith('http://') || t.startsWith('https://') || t.startsWith('data:')) return t;
  return `${getApiBaseUrl()}${t.startsWith('/') ? t : `/${t}`}`;
}

/** Absolute URL for admin video previews (paths like /uploads/services/…). */
function resolveVideoEditingMediaUrl(src) {
  if (src == null || src === '') return '';
  const t = String(src).trim();
  if (!t) return '';
  if (t.startsWith('http://') || t.startsWith('https://') || t.startsWith('data:')) return t;
  return `${getApiBaseUrl()}${t.startsWith('/') ? t : `/${t}`}`;
}

/**
 * Plain <input> cannot receive image binary from sites like Flaticon.
 * Handle clipboard image, HTML with <img src>, or let text URL paste normally.
 */
async function handleVideoEditingToolIconPaste(e, index, setFormData) {
  const cd = e.clipboardData;
  if (!cd) return;

  const setIcon = (url) => {
    setFormData((prev) => {
      const arr = [...(prev.video_editing_tools || [])];
      arr[index] = { ...arr[index], icon: url };
      return { ...prev, video_editing_tools: arr };
    });
  };

  try {
    if (cd.files && cd.files.length > 0) {
      const f = cd.files[0];
      if (f.type.startsWith('image/')) {
        e.preventDefault();
        const { url } = await servicesAPI.uploadImage(f);
        setIcon(url);
        showSuccess('Icon uploaded');
        return;
      }
    }

    const html = cd.getData('text/html');
    if (html && html.trim()) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const img = doc.querySelector('img');
      const src = img?.getAttribute('src');
      if (src) {
        e.preventDefault();
        if (src.startsWith('data:')) {
          const file = dataUrlToFile(src, 'icon.png');
          const { url } = await servicesAPI.uploadImage(file);
          setIcon(url);
          showSuccess('Icon uploaded');
          return;
        }
        if (src.startsWith('blob:')) {
          const res = await fetch(src);
          const blob = await res.blob();
          const file = new File([blob], 'icon.png', { type: blob.type || 'image/png' });
          const { url } = await servicesAPI.uploadImage(file);
          setIcon(url);
          showSuccess('Icon uploaded');
          return;
        }
        if (src.startsWith('http') || src.startsWith('//')) {
          const absolute = src.startsWith('//') ? `https:${src}` : src;
          setIcon(absolute);
          showSuccess('Icon URL set — save the service to keep it');
          return;
        }
      }
    }
  } catch (err) {
    showError(err.response?.data?.error || err.message || 'Could not paste icon');
  }
}

const ServiceForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price_starting_at: '',
    service_number: '',
    slug: '',
    route: '',
    order_index: 0,
    is_active: true,
    // Content fields
    hero_title: '',
    hero_subtitle: '',
    section1_title: '',
    section1_content: '',
    section2_title: '',
    section2_content: '',
    cta_title: '',
    cta_subtitle: '',
    booking_enabled: true,
    why_choose_us: [
      { title: '', description: '' },
      { title: '', description: '' },
      { title: '', description: '' },
      { title: '', description: '' }
    ],
    // Graphics Design specific fields (used when slug === 'graphics-design')
    graphics_portfolio_categories: [
      'All Work',
      'Branding & Identity',
      'Digital & Social',
      'Print & Editorial'
    ],
    graphics_portfolio_items: [],
    graphics_wip_enabled: true,
    graphics_typography_showcase: [
      { text: 'A', size: 'large' },
      { text: 'B', size: 'medium' },
      { text: 'C', size: 'small' },
      { text: 'D', size: 'medium' }
    ],
    // Web Design specific fields
    web_hero_title: 'INNOVATIVE DESIGN SOLUTIONS FOR YOUR BUSINESS',
    web_services: {
      sectionSubtitle: '|| Our Service',
      sectionTitle: 'Services We Provide For Our Clients',
      cards: [
        { title: 'Development', icon: 'dev', skills: ['HTML/CSS', 'Javascript', 'WordPress', 'Animation', 'React'] },
        { title: 'UI/UX Design', icon: 'ui', skills: ['Wireframing', 'Prototyping', 'Figma', 'User Research', 'Interaction Design'] },
        { title: 'Illustration', icon: 'illus', skills: ['Digital Art', 'Vector Graphics', 'Sketching', 'Adobe Illustrator', 'Branding'] },
        { title: 'SEO Marketing', icon: 'seo', skills: ['White Hot SEO', 'Black Hot SEO', 'Grey Hat SEO', 'Negative SEO', 'On-Page SEO'] },
        { title: 'Brand Identity', icon: 'brand', skills: ['Brand Architecture', 'Brand Authenticity', 'Brand Awareness', 'Brand Management', 'Brand Metrics'] },
        { title: 'Data Analysis', icon: 'analysis', skills: ['Diagnostic Analysis', 'Prescriptive Analysis', 'Predictive Analysis', 'Sales Forecasting', 'Risk Assessment'] }
      ]
    },
    web_projects: {
      sectionSubtitle: '|| Awesome Works',
      sectionTitle: 'Our Complete Projects',
      items: [
        { image: '', title: 'E-commerce Website', subtitle: 'React & Node.js' },
        { image: '', title: 'Portfolio Site', subtitle: 'Next.js & Tailwind' },
        { image: '', title: 'Mobile App', subtitle: 'React Native' },
        { image: '', title: 'Dashboard', subtitle: 'Vue.js & Firebase' }
      ]
    },
    web_service_details: [],
    // PA System fields
    pa_hero_video: '',
    pa_hero_title: '',
    pa_benefits: [
      { title: 'High-Quality Sound', description: 'Professional-grade audio equipment for clear and powerful sound.' },
      { title: 'Affordable Pricing', description: 'Competitive rental rates with flexible packages to fit your budget.' },
      { title: 'Expert Setup & Support', description: 'Our team ensures seamless setup and provides on-site assistance.' },
      { title: 'Flexible Rental Plans', description: 'Short-term and long-term rentals available to suit any event size.' }
    ],
    pa_equipment_services: [],
    pa_features: [],
    pa_event_types: [],
    pa_how_it_works: [],
    // Event Decoration, Video Editing, Photo Editing, DJing - populated on fetch
    decoration_hero_title: '', decoration_hero_images: { carousel: [], mosaic: [] },
    decoration_pillars: [], decoration_gallery_projects: [], decoration_process_steps: [],
    decoration_testimonial: null, decoration_packages: [],
    video_editing_hero_video: '', video_editing_hero_title: '', video_editing_portfolio: [], video_editing_workflow: [], video_editing_before_after: [], video_editing_tools: [],
    photo_editing_hero_title: '', photo_editing_before_after: [], photo_editing_portfolio: [], photo_editing_services: [], photo_editing_tools: [],
    djing_hero_title: '', djing_audio_title: '', djing_categories: [], djing_gear: [], djing_process: [],
    custom_sections: []
  });

  const defaultWebServiceDetails = [
    { category: 'Web Development', subtitle: '|| Web Development', title: 'Comprehensive Solutions for Modern Businesses', content: ["In today's fast-paced digital landscape, having a robust online presence is essential for businesses of all sizes. Our team specializes in delivering custom web development solutions tailored to meet the unique needs of each client.", "With a focus on user experience and performance, we build websites that are intuitive, engaging, and optimized for search engines.", "Partner with us to transform your online presence and achieve your business goals. Our comprehensive range of services includes front-end and back-end development, e-commerce solutions, and content management systems."], details: ['HTML/CSS', 'Javascript', 'WordPress', 'Animation', 'React'], imageUrl: null },
    { category: 'UI/UX Design', subtitle: '|| UI/UX Design', title: 'Crafting Intuitive and Engaging User Experiences', content: ["UI/UX design is at the heart of every great digital experience. Our team creates visually appealing and user-friendly interfaces that enhance user satisfaction and drive engagement.", "Through wireframing, prototyping, and user research, we design experiences that meet user needs and exceed their expectations.", "Whether you are launching a new product or redesigning an existing one, our UI/UX design services ensure that your users have a seamless and enjoyable experience."], details: ['Wireframing', 'Prototyping', 'Figma', 'User Research', 'Interaction Design'], imageUrl: null },
    { category: 'Illustration', subtitle: '|| Illustration', title: 'Bringing Ideas to Life Through Creative Illustrations', content: ["Our illustration services transform concepts into visually compelling art that communicates your message effectively.", "We specialize in digital art, vector graphics, and sketching, using tools like Adobe Illustrator to create high-quality illustrations.", "Whether it's for branding, advertising, or editorial content, our illustrations add a unique touch to your projects."], details: ['Digital Art', 'Vector Graphics', 'Sketching', 'Adobe Illustrator', 'Branding'], imageUrl: null },
    { category: 'SEO Marketing', subtitle: '|| SEO Marketing', title: 'Optimizing Your Online Presence for Maximum Visibility', content: ["Our SEO marketing services are designed to improve your website's visibility on search engines, driving more traffic and increasing conversions.", "We offer a range of services, including White Hat SEO, Black Hat SEO, Grey Hat SEO, and On-Page SEO.", "Our strategies are tailored to meet your specific needs, ensuring that you achieve the best possible results."], details: ['White Hot SEO', 'Black Hot SEO', 'Grey Hat SEO', 'Negative SEO', 'On-Page SEO'], imageUrl: null },
    { category: 'Brand Identity', subtitle: '|| Brand Identity', title: 'Building Strong and Recognizable Brands', content: ["A strong brand identity is crucial for building trust and loyalty with your audience. Our branding services help you establish a clear and consistent brand image.", "We specialize in brand architecture, authenticity, awareness, management, and metrics.", "Our comprehensive approach ensures that your brand stands out in the competitive marketplace."], details: ['Brand Architecture', 'Brand Authenticity', 'Brand Awareness', 'Brand Management', 'Brand Metrics'], imageUrl: null },
    { category: 'Data Analysis', subtitle: '|| Data Analysis', title: 'Turning Data into Actionable Insights', content: ["Our data analysis services help you make informed decisions by providing actionable insights from your data.", "We specialize in diagnostic analysis, prescriptive analysis, predictive analysis, sales forecasting, and risk assessment.", "With our expertise, you can leverage data to drive growth and achieve your business objectives."], details: ['Diagnostic Analysis', 'Prescriptive Analysis', 'Predictive Analysis', 'Sales Forecasting', 'Risk Assessment'], imageUrl: null }
  ];

  const [images, setImages] = useState({
    hero_image: null,
    section1_image: null,
    section2_image: null,
    gallery_images: [],
    // Graphics Design WIP images
    graphics_wip_sketch_image: null,
    graphics_wip_final_image: null,
    graphics_portfolio_items: [],
    web_hero_image: null,
    web_project_images: Array(4).fill(null),
    web_detail_images: Array(6).fill(null)
  });

  const [imagePreviews, setImagePreviews] = useState({
    hero_image: null,
    section1_image: null,
    section2_image: null,
    gallery_images: [],
    graphics_wip_sketch_image: null,
    graphics_wip_final_image: null,
    graphics_portfolio_items: [],
    web_hero_image: null,
    web_project_images: Array(4).fill(null),
    web_detail_images: Array(6).fill(null)
  });

  const [existingImages, setExistingImages] = useState({
    hero_image: null,
    section1_image: null,
    section2_image: null,
    gallery_images: [],
    graphics_wip_sketch_image: null,
    graphics_wip_final_image: null,
    web_hero_image: null,
    web_project_images: Array(4).fill(null),
    web_detail_images: Array(6).fill(null)
  });

  // Track which existing gallery images to keep (for deletion tracking)
  const [keptExistingGalleryImages, setKeptExistingGalleryImages] = useState([]);

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState('');
  const [isVideographService, setIsVideographService] = useState(false);
  const [isGraphicsDesignService, setIsGraphicsDesignService] = useState(false);
  const [isWebDesignService, setIsWebDesignService] = useState(false);
  const [isEventDecorationService, setIsEventDecorationService] = useState(false);
  const [isVideoEditingService, setIsVideoEditingService] = useState(false);

  // Simple preview component reused for images/videos
  const ImagePreview = ({ preview, label, onRemove, fileName, isNew = false }) => {
    if (!preview) return null;
    return (
      <div className="image-preview-container existing-image-preview">
        <div className="image-preview-wrapper">
          <img
            src={preview}
            alt={label}
            className="image-preview-img"
          />
          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              className="remove-image-btn"
              title="Remove file"
            >
              ✕
            </button>
          )}
        </div>
        {fileName && <p className="image-filename">{fileName}</p>}
        <small>{isNew ? 'New image preview' : 'Current image'}</small>
      </div>
    );
  };

  useEffect(() => {
    setImagePreviews({
      hero_image: null,
      section1_image: null,
      section2_image: null,
      gallery_images: [],
      graphics_wip_sketch_image: null,
      graphics_wip_final_image: null,
      graphics_portfolio_items: [],
      web_hero_image: null,
      web_project_images: Array(4).fill(null),
      web_detail_images: Array(6).fill(null)
    });
    setImages({
      hero_image: null,
      section1_image: null,
      section2_image: null,
      gallery_images: [],
      graphics_wip_sketch_image: null,
      graphics_wip_final_image: null,
      web_hero_image: null,
      web_project_images: Array(4).fill(null),
      web_detail_images: Array(6).fill(null)
    });
    setKeptExistingGalleryImages([]);
    setIsVideographService(false);
    setIsWebDesignService(false);

    if (isEditing) {
      fetchService();
    }
  }, [id, isEditing]);

  // Update service-type flags when formData changes
  useEffect(() => {
    const isVideograph = formData.slug === 'videograph';
    const isVideoEditing =
      formData.slug === 'video-editing' || formData.route === '/video-editing';
    const isGraphics = formData.slug === 'graphics-design' || formData.name?.toLowerCase().includes('graphics');
    const isWebDesign = formData.slug === 'web-design' || formData.name?.toLowerCase().includes('web design');
    const isEventDec =
      formData.slug === 'event-decoration' ||
      formData.route === '/event-decoration';
    setIsVideographService(isVideograph);
    setIsVideoEditingService(isVideoEditing);
    setIsGraphicsDesignService(isGraphics);
    setIsWebDesignService(isWebDesign);
    setIsEventDecorationService(isEventDec);
  }, [formData.slug, formData.name, formData.route]);

  const fetchService = async () => {
    try {
      setFetching(true);
      const response = await axios.get(`/services/${id}`);
      const service = response.data;
      
      const isVideo = service.slug === 'videograph';
      const isGraphicsDesign = service.slug === 'graphics-design' || service.name?.toLowerCase().includes('graphics');
      const isWebDesign = service.slug === 'web-design' || service.name?.toLowerCase().includes('web design');

      const defaultWebServices = {
        sectionSubtitle: '|| Our Service',
        sectionTitle: 'Services We Provide For Our Clients',
        cards: [
          { title: 'Development', icon: 'dev', skills: ['HTML/CSS', 'Javascript', 'WordPress', 'Animation', 'React'] },
          { title: 'UI/UX Design', icon: 'ui', skills: ['Wireframing', 'Prototyping', 'Figma', 'User Research', 'Interaction Design'] },
          { title: 'Illustration', icon: 'illus', skills: ['Digital Art', 'Vector Graphics', 'Sketching', 'Adobe Illustrator', 'Branding'] },
          { title: 'SEO Marketing', icon: 'seo', skills: ['White Hot SEO', 'Black Hot SEO', 'Grey Hat SEO', 'Negative SEO', 'On-Page SEO'] },
          { title: 'Brand Identity', icon: 'brand', skills: ['Brand Architecture', 'Brand Authenticity', 'Brand Awareness', 'Brand Management', 'Brand Metrics'] },
          { title: 'Data Analysis', icon: 'analysis', skills: ['Diagnostic Analysis', 'Prescriptive Analysis', 'Predictive Analysis', 'Sales Forecasting', 'Risk Assessment'] }
        ]
      };
      const defaultWebProjects = {
        sectionSubtitle: '|| Awesome Works',
        sectionTitle: 'Our Complete Projects',
        items: [
          { image: '', title: 'E-commerce Website', subtitle: 'React & Node.js' },
          { image: '', title: 'Portfolio Site', subtitle: 'Next.js & Tailwind' },
          { image: '', title: 'Mobile App', subtitle: 'React Native' },
          { image: '', title: 'Dashboard', subtitle: 'Vue.js & Firebase' }
        ]
      };

      setFormData({
        name: service.name || '',
        description: service.description || '',
        price_starting_at: service.price_starting_at || '',
        service_number: service.service_number || '',
        slug: service.slug || '',
        route: service.route || '',
        order_index: service.order_index || 0,
        is_active: service.is_active === 1 || service.is_active === true,
        hero_title: service.hero_title || '',
        hero_subtitle: service.hero_subtitle || '',
        section1_title: service.section1_title || '',
        section1_content: service.section1_content || '',
        section2_title: service.section2_title || '',
        section2_content: service.section2_content || '',
        cta_title: service.cta_title || '',
        cta_subtitle: service.cta_subtitle || '',
        booking_enabled: service.booking_enabled !== undefined ? (service.booking_enabled === 1 || service.booking_enabled === true) : true,
        why_choose_us: service.why_choose_us && Array.isArray(service.why_choose_us) && service.why_choose_us.length > 0
          ? service.why_choose_us
          : [
              { title: '', description: '' },
              { title: '', description: '' },
              { title: '', description: '' },
              { title: '', description: '' }
            ],
        // Graphics Design specific fields – parse if string, use array if already parsed, fall back to defaults
        graphics_portfolio_categories: (() => {
          if (!isGraphicsDesign) return ['All Work', 'Branding & Identity', 'Digital & Social', 'Print & Editorial'];
          if (Array.isArray(service.graphics_portfolio_categories) && service.graphics_portfolio_categories.length > 0) {
            return service.graphics_portfolio_categories;
          }
          if (typeof service.graphics_portfolio_categories === 'string') {
            try {
              const parsed = JSON.parse(service.graphics_portfolio_categories);
              if (Array.isArray(parsed) && parsed.length > 0) return parsed;
            } catch (e) {
              console.error('Error parsing graphics_portfolio_categories:', e);
            }
          }
          return ['All Work', 'Branding & Identity', 'Digital & Social', 'Print & Editorial'];
        })(),
        graphics_portfolio_items: (() => {
          const slugify = (n) => {
            const s = (typeof n === 'string' ? n : n?.name || '').toLowerCase().trim();
            if (!s) return 'branding';
            if (s.includes('all work') || s.includes('all works')) return 'all';
            if (s.includes('brand') || s.includes('identity')) return 'branding';
            if (s.includes('digital') || s.includes('social')) return 'digital';
            if (s.includes('print') || s.includes('editorial')) return 'print';
            if (['branding', 'digital', 'print'].includes(s)) return s;
            return s.replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') || 'branding';
          };
          let items = [];
          if (Array.isArray(service.graphics_portfolio_items) && service.graphics_portfolio_items.length > 0) {
            items = service.graphics_portfolio_items;
          } else if (typeof service.graphics_portfolio_items === 'string') {
            try {
              const parsed = JSON.parse(service.graphics_portfolio_items);
              if (Array.isArray(parsed) && parsed.length > 0) items = parsed;
            } catch (e) {
              console.error('Error parsing graphics_portfolio_items:', e);
            }
          }
          return items.map((item) => ({
            ...item,
            category: item.category ? slugify(item.category) : 'branding'
          }));
        })(),
        graphics_wip_enabled: isGraphicsDesign
          ? (service.graphics_wip_enabled === 1 || service.graphics_wip_enabled === true)
          : false,
        graphics_typography_showcase: (() => {
          if (!isGraphicsDesign) return [{ text: 'A', size: 'large' }, { text: 'B', size: 'medium' }, { text: 'C', size: 'small' }, { text: 'D', size: 'medium' }];
          if (Array.isArray(service.graphics_typography_showcase) && service.graphics_typography_showcase.length > 0) {
            return service.graphics_typography_showcase;
          }
          if (typeof service.graphics_typography_showcase === 'string') {
            try {
              const parsed = JSON.parse(service.graphics_typography_showcase);
              if (Array.isArray(parsed) && parsed.length > 0) return parsed;
            } catch (e) {
              console.error('Error parsing graphics_typography_showcase:', e);
            }
          }
          return [{ text: 'A', size: 'large' }, { text: 'B', size: 'medium' }, { text: 'C', size: 'small' }, { text: 'D', size: 'medium' }];
        })(),
        web_hero_title: isWebDesign ? (service.web_hero_title || 'INNOVATIVE DESIGN SOLUTIONS FOR YOUR BUSINESS') : 'INNOVATIVE DESIGN SOLUTIONS FOR YOUR BUSINESS',
        web_services: isWebDesign ? (() => {
          const ws = service.web_services;
          if (ws && typeof ws === 'object' && ws.cards) return ws;
          if (typeof ws === 'string') { try { const p = JSON.parse(ws); if (p && p.cards) return p; } catch (e) {} }
          return defaultWebServices;
        })() : defaultWebServices,
        web_projects: isWebDesign ? (() => {
          const wp = service.web_projects;
          if (wp && typeof wp === 'object' && wp.items) return wp;
          if (typeof wp === 'string') { try { const p = JSON.parse(wp); if (p && p.items) return p; } catch (e) {} }
          return defaultWebProjects;
        })() : defaultWebProjects,
        web_service_details: isWebDesign ? (() => {
          const wsd = service.web_service_details;
          if (Array.isArray(wsd) && wsd.length > 0) return wsd;
          if (typeof wsd === 'string') { try { const p = JSON.parse(wsd); if (Array.isArray(p) && p.length > 0) return p; } catch (e) {} }
          return defaultWebServiceDetails;
        })() : [],
        pa_hero_video: service.pa_hero_video || '',
        pa_hero_title: service.pa_hero_title || '',
        pa_benefits: (() => {
          const v = service.pa_benefits;
          if (Array.isArray(v) && v.length > 0) return v;
          if (typeof v === 'string') { try { const p = JSON.parse(v); if (Array.isArray(p)) return p; } catch (e) {} }
          return [
            { title: 'High-Quality Sound', description: 'Professional-grade audio equipment.' },
            { title: 'Affordable Pricing', description: 'Competitive rental rates.' },
            { title: 'Expert Setup & Support', description: 'Seamless setup and on-site assistance.' },
            { title: 'Flexible Rental Plans', description: 'Short and long-term rentals.' }
          ];
        })(),
        pa_equipment_services: (() => {
          const v = service.pa_equipment_services;
          if (Array.isArray(v) && v.length > 0) return v;
          if (typeof v === 'string') { try { const p = JSON.parse(v); if (Array.isArray(p) && p.length > 0) return p; } catch (e) {} }
          return [
            { title: 'Speaker', description: 'High-quality speakers for any event.', img: '' },
            { title: 'Microphone', description: 'Wireless and wired microphone rentals.', img: '' },
            { title: 'DJ Equipment', description: 'Professional DJ gear for all occasions.', img: '' },
            { title: 'Full Event Setup', description: 'Complete sound setup for any venue.', img: '' },
            { title: 'Sound Engineering', description: 'Expert audio engineers for a flawless experience.', img: '' },
            { title: 'Mixer', description: 'Professional audio mixers for balanced and high-quality sound control.', img: '' }
          ];
        })(),
        pa_features: (() => {
          const v = service.pa_features;
          if (Array.isArray(v) && v.length > 0) return v;
          if (typeof v === 'string') { try { const p = JSON.parse(v); if (Array.isArray(p) && p.length > 0) return p; } catch (e) {} }
          return [
            { title: 'Premium Audio Quality', items: ['High-quality speakers & microphones', 'Clear, distortion-free sound', 'Perfect for any event size'] },
            { title: 'Professional Support', items: ['Expert setup & assistance', 'On-site troubleshooting', '24/7 customer support'] },
            { title: 'Flexible & Affordable', items: ['Budget-friendly pricing', 'Short-term & long-term rentals', 'Custom rental packages'] },
            { title: 'Wide Range of Equipment', items: ['PA systems, mixers, DJ gear', 'Indoor & outdoor compatibility', 'Reliable & well-maintained gear'] }
          ];
        })(),
        pa_event_types: (() => {
          const v = service.pa_event_types;
          if (Array.isArray(v) && v.length > 0) return v;
          if (typeof v === 'string') { try { const p = JSON.parse(v); if (Array.isArray(p) && p.length > 0) return p; } catch (e) {} }
          return [{ title: 'Weddings', img: '' }, { title: 'Corporate Events', img: '' }, { title: 'Concerts', img: '' }, { title: 'Conferences', img: '' }, { title: 'Parties', img: '' }, { title: 'Live', img: '' }];
        })(),
        pa_how_it_works: (() => {
          const v = service.pa_how_it_works;
          if (Array.isArray(v) && v.length > 0) return v;
          if (typeof v === 'string') { try { const p = JSON.parse(v); if (Array.isArray(p) && p.length > 0) return p; } catch (e) {} }
          return [
            { icon: '🔍', title: 'Choose Equipment', desc: 'Browse our PA systems and select what suits your event.' },
            { icon: '📅', title: 'Book Your Rental', desc: 'Secure your equipment by booking online or contacting us.' },
            { icon: '🚚', title: 'Delivery & Setup', desc: 'We deliver and set up the equipment at your venue.' },
            { icon: '🎉', title: 'Enjoy Your Event', desc: 'Experience high-quality sound for a successful event.' }
          ];
        })(),
        decoration_hero_images: (() => {
          const v = service.decoration_hero_images;
          const empty = { carousel: [], mosaic: [] };
          if (v && typeof v === 'object' && !Array.isArray(v)) {
            return {
              carousel: Array.isArray(v.carousel) ? [...v.carousel] : [],
              mosaic: Array.isArray(v.mosaic) ? [...v.mosaic] : [],
            };
          }
          if (typeof v === 'string') {
            try {
              const p = JSON.parse(v);
              if (p && typeof p === 'object') {
                return {
                  carousel: Array.isArray(p.carousel) ? p.carousel : [],
                  mosaic: Array.isArray(p.mosaic) ? p.mosaic : [],
                };
              }
            } catch (e) { /* ignore */ }
          }
          return empty;
        })(),
        decoration_pillars: (() => { const v = service.decoration_pillars; if (Array.isArray(v)) return v; if (typeof v === 'string') { try { return JSON.parse(v) || []; } catch (e) {} } return []; })(),
        decoration_gallery_projects: (() => { const v = service.decoration_gallery_projects; if (Array.isArray(v)) return v; if (typeof v === 'string') { try { return JSON.parse(v) || []; } catch (e) {} } return []; })(),
        decoration_process_steps: (() => { const v = service.decoration_process_steps; if (Array.isArray(v)) return v; if (typeof v === 'string') { try { return JSON.parse(v) || []; } catch (e) {} } return []; })(),
        decoration_testimonial: (() => { const v = service.decoration_testimonial; if (v && typeof v === 'object') return v; if (typeof v === 'string') { try { return JSON.parse(v); } catch (e) {} } return null; })(),
        decoration_packages: (() => { const v = service.decoration_packages; if (Array.isArray(v)) return v; if (typeof v === 'string') { try { return JSON.parse(v) || []; } catch (e) {} } return []; })(),
        video_editing_portfolio: (() => { const v = service.video_editing_portfolio; if (Array.isArray(v)) return v; if (typeof v === 'string') { try { return JSON.parse(v) || []; } catch (e) {} } return []; })(),
        video_editing_workflow: (() => { const v = service.video_editing_workflow; if (Array.isArray(v)) return v; if (typeof v === 'string') { try { return JSON.parse(v) || []; } catch (e) {} } return []; })(),
        video_editing_before_after: (() => { const v = service.video_editing_before_after; if (Array.isArray(v)) return v; if (typeof v === 'string') { try { return JSON.parse(v) || []; } catch (e) {} } return []; })(),
        video_editing_tools: (() => { const v = service.video_editing_tools; if (Array.isArray(v)) return v; if (typeof v === 'string') { try { return JSON.parse(v) || []; } catch (e) {} } return []; })(),
        photo_editing_before_after: (() => { const v = service.photo_editing_before_after; if (Array.isArray(v)) return v; if (typeof v === 'string') { try { return JSON.parse(v) || []; } catch (e) {} } return []; })(),
        photo_editing_portfolio: (() => { const v = service.photo_editing_portfolio; if (Array.isArray(v)) return v; if (typeof v === 'string') { try { return JSON.parse(v) || []; } catch (e) {} } return []; })(),
        photo_editing_services: (() => { const v = service.photo_editing_services; if (Array.isArray(v)) return v; if (typeof v === 'string') { try { return JSON.parse(v) || []; } catch (e) {} } return []; })(),
        photo_editing_tools: (() => { const v = service.photo_editing_tools; if (Array.isArray(v)) return v; if (typeof v === 'string') { try { return JSON.parse(v) || []; } catch (e) {} } return []; })(),
        djing_categories: (() => { const v = service.djing_categories; if (Array.isArray(v)) return v; if (typeof v === 'string') { try { return JSON.parse(v) || []; } catch (e) {} } return []; })(),
        djing_gear: (() => { const v = service.djing_gear; if (Array.isArray(v)) return v; if (typeof v === 'string') { try { return JSON.parse(v) || []; } catch (e) {} } return []; })(),
        djing_process: (() => { const v = service.djing_process; if (Array.isArray(v)) return v; if (typeof v === 'string') { try { return JSON.parse(v) || []; } catch (e) {} } return []; })(),
        decoration_hero_title: service.decoration_hero_title || '',
        video_editing_hero_video: service.video_editing_hero_video || '',
        video_editing_hero_title: service.video_editing_hero_title || '',
        photo_editing_hero_title: service.photo_editing_hero_title || '',
        djing_hero_title: service.djing_hero_title || '',
        djing_audio_title: service.djing_audio_title || '',
        custom_sections: (() => {
          const v = service.custom_sections;
          let arr = [];
          if (Array.isArray(v) && v.length > 0) arr = v;
          else if (typeof v === 'string') { try { const p = JSON.parse(v); if (Array.isArray(p)) arr = p; } catch (e) {} }
          // Migrate old format (1 wedding_fashion with 3 images) to new (3 wedding_fashion items)
          const weddingItems = arr.filter(s => s.type === 'wedding_fashion');
          if (weddingItems.length === 1 && (weddingItems[0].images?.length || 0) >= 2) {
            const w = weddingItems[0];
            const imgs = w.images || ['', '', ''];
            const others = arr.filter(s => s.type !== 'wedding_fashion');
            arr = [
              { type: 'wedding_fashion', title: w.title || '', content: w.content || '', images: [imgs[0] || ''] },
              { type: 'wedding_fashion', title: '', content: '', images: [imgs[1] || ''] },
              { type: 'wedding_fashion', title: '', content: '', images: [imgs[2] || ''] },
              ...others
            ];
          }
          return arr;
        })()
      });

      // Helper function to get image URL
      const getImageUrl = (imagePath) => {
        if (!imagePath) return null;
        if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
          return imagePath;
        }
        if (imagePath.startsWith('/uploads')) {
          return `${getApiBaseUrl()}${imagePath}`;
        }
        // Frontend assets can't be displayed in admin
        if (imagePath.startsWith('/assests') || imagePath.startsWith('/assets')) {
          return null;
        }
        return `${getApiBaseUrl()}${imagePath.startsWith('/') ? '' : '/'}${imagePath}`;
      };

      // Parse gallery_images if it's a string
      let galleryImages = [];
      if (service.gallery_images) {
        if (typeof service.gallery_images === 'string') {
          try {
            galleryImages = JSON.parse(service.gallery_images);
          } catch (e) {
            console.error('Error parsing gallery_images:', e);
            galleryImages = [];
          }
        } else if (Array.isArray(service.gallery_images)) {
          galleryImages = service.gallery_images;
        }
      }
      
      setExistingImages({
        hero_image: service.hero_image,
        section1_image: service.section1_image,
        section2_image: service.section2_image,
        gallery_images: galleryImages,
        graphics_wip_sketch_image: service.graphics_wip_sketch_image || null,
        graphics_wip_final_image: service.graphics_wip_final_image || null,
        web_hero_image: service.web_hero_image || null
      });

      // Set existing images/videos as previews (so they show immediately)
      const galleryPreviewUrls = galleryImages.map(img => getImageUrl(img)).filter(Boolean);
      
      // Set portfolio items previews
      const portfolioItems = isGraphicsDesign && Array.isArray(service.graphics_portfolio_items) 
        ? service.graphics_portfolio_items 
        : (typeof service.graphics_portfolio_items === 'string' 
          ? (() => { try { return JSON.parse(service.graphics_portfolio_items); } catch { return []; } })()
          : []);
      const portfolioItemPreviews = portfolioItems.map(item => 
        item.image ? getImageUrl(item.image) : null
      ).filter(Boolean);
      
      const projectImgs = Array(4).fill(null);
      if (service.web_projects) {
        const wp = typeof service.web_projects === 'string' ? JSON.parse(service.web_projects) : service.web_projects;
        if (wp.items) wp.items.forEach((item, i) => { if (item.image) projectImgs[i] = item.image; });
      }

      const detailImgs = Array(6).fill(null);
      if (service.web_service_details) {
        const wsd = typeof service.web_service_details === 'string' ? JSON.parse(service.web_service_details) : service.web_service_details;
        if (Array.isArray(wsd)) wsd.forEach((item, i) => { if (item.imageUrl) detailImgs[i] = item.imageUrl; });
      }

      setExistingImages({
        hero_image: service.hero_image,
        section1_image: service.section1_image,
        section2_image: service.section2_image,
        gallery_images: galleryImages,
        graphics_wip_sketch_image: service.graphics_wip_sketch_image || null,
        graphics_wip_final_image: service.graphics_wip_final_image || null,
        web_hero_image: service.web_hero_image || null,
        web_project_images: projectImgs,
        web_detail_images: detailImgs
      });
      
      setImagePreviews({
        hero_image: getImageUrl(service.hero_image),
        section1_image: getImageUrl(service.section1_image),
        section2_image: getImageUrl(service.section2_image),
        gallery_images: galleryPreviewUrls,
        graphics_wip_sketch_image: getImageUrl(service.graphics_wip_sketch_image),
        graphics_wip_final_image: getImageUrl(service.graphics_wip_final_image),
        graphics_portfolio_items: portfolioItemPreviews,
        web_hero_image: getImageUrl(service.web_hero_image),
        web_project_images: projectImgs.map(img => getImageUrl(img)),
        web_detail_images: detailImgs.map(img => getImageUrl(img))
      });
      
      // Initialize kept existing gallery images (all are kept initially)
      setKeptExistingGalleryImages(Array.isArray(galleryImages) ? galleryImages : []);
      
      setIsVideographService(isVideo);
      setIsGraphicsDesignService(isGraphicsDesign);
      setIsWebDesignService(isWebDesign);
    } catch (err) {
      console.error('Error fetching service:', err);
      setError('Failed to load service data');
      showError('Failed to load service data');
    } finally {
      setFetching(false);
    }
  };

  const generateSlug = (name) => {
    if (!name || typeof name !== 'string') return '';
    return name.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  };

  // Map category name to slug (must match frontend graphics.jsx nameToSlug)
  const normalizeCategoryToSlug = (name) => {
    const n = (typeof name === 'string' ? name : name?.name || '').toLowerCase().trim();
    if (!n) return 'branding';
    if (n.includes('all work') || n.includes('all works')) return 'all';
    if (n.includes('brand') || n.includes('identity')) return 'branding';
    if (n.includes('digital') || n.includes('social')) return 'digital';
    if (n.includes('print') || n.includes('editorial')) return 'print';
    if (['branding', 'digital', 'print'].includes(n)) return n;
    return n.replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') || 'branding';
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    let newValue = type === 'checkbox' ? checked : value;
    
    // Auto-generate slug from name (user never edits slug)
    if (name === 'name') {
      const slug = generateSlug(value);
      setFormData(prev => ({
        ...prev,
        name: value,
        slug: slug || prev.slug,
        route: slug ? `/${slug}` : prev.route
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
  };

  const handleImageChange = (e) => {
    const { name, files } = e.target;
    
    if (!files || files.length === 0) return;
    
    if (name === 'gallery_images') {
      const newFiles = Array.from(files);
      
      // Validate file sizes (100MB max)
      const maxSize = 100 * 1024 * 1024;
      const invalidFiles = newFiles.filter(file => file.size > maxSize);
      if (invalidFiles.length > 0) {
        showError(`Some files exceed 100MB limit. Please select smaller files.`);
        e.target.value = '';
        return;
      }
      
      // Add new files to existing ones (don't replace)
      setImages(prev => ({
        ...prev,
        gallery_images: [...prev.gallery_images, ...newFiles]
      }));
      
      // Create preview URLs for new files and add to existing previews
      const newPreviewUrls = newFiles.map(file => URL.createObjectURL(file));
      setImagePreviews(prev => ({
        ...prev,
        gallery_images: [...(prev.gallery_images || []), ...newPreviewUrls]
      }));
      
      // Reset file input to allow selecting the same file again
      e.target.value = '';
    } else if (name.startsWith('web_project_item_')) {
      const index = parseInt(name.replace('web_project_item_', ''));
      const file = files[0];
      if (!file) return;

      setImages(prev => {
        const newImgs = [...(prev.web_project_images || Array(4).fill(null))];
        newImgs[index] = file;
        return { ...prev, web_project_images: newImgs };
      });
      setImagePreviews(prev => {
        const newPreviews = [...(prev.web_project_images || Array(4).fill(null))];
        if (newPreviews[index] && newPreviews[index].startsWith('blob:')) URL.revokeObjectURL(newPreviews[index]);
        newPreviews[index] = URL.createObjectURL(file);
        return { ...prev, web_project_images: newPreviews };
      });
    } else if (name.startsWith('web_detail_block_')) {
      const index = parseInt(name.replace('web_detail_block_', ''));
      const file = files[0];
      if (!file) return;

      setImages(prev => {
        const newImgs = [...(prev.web_detail_images || Array(6).fill(null))];
        newImgs[index] = file;
        return { ...prev, web_detail_images: newImgs };
      });
      setImagePreviews(prev => {
        const newPreviews = [...(prev.web_detail_images || Array(6).fill(null))];
        if (newPreviews[index] && newPreviews[index].startsWith('blob:')) URL.revokeObjectURL(newPreviews[index]);
        newPreviews[index] = URL.createObjectURL(file);
        return { ...prev, web_detail_images: newPreviews };
      });
    } else {
      const file = files[0];
      
      // Validate file size (100MB max)
      const maxSize = 100 * 1024 * 1024;
      if (file.size > maxSize) {
        showError('File exceeds 100MB limit. Please select a smaller file.');
        e.target.value = '';
        return;
      }
      
      // Validate file type based on service
      const isVideograph = formData.slug === 'videograph';
      const validVideoTypes = ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime', 'video/x-msvideo'];
      
      if (name === 'hero_image' && isVideograph) {
        // For videograph hero, allow both images and videos
        if (!isAllowedImageFile(file) && !validVideoTypes.includes(file.type)) {
          showError('Invalid file type. Please select an image or video.');
          e.target.value = '';
          return;
        }
      } else if (name === 'gallery_images' && isVideograph) {
        // For videograph gallery, only allow videos
        if (!validVideoTypes.includes(file.type)) {
          showError('Invalid file type. Please select a video (MP4, WebM, etc.).');
          e.target.value = '';
          return;
        }
      } else {
        // For other services, only allow images (used for Graphics Design WIP images and others)
        if (!isAllowedImageFile(file)) {
          showError('Invalid file type. Please select a valid image (e.g. JPEG, PNG, WebP, AVIF).');
          e.target.value = '';
          return;
        }
      }
      
      // Revoke old preview URL if it's a blob URL
      setImagePreviews(prev => {
        if (prev[name] && prev[name].startsWith('blob:')) {
          URL.revokeObjectURL(prev[name]);
        }
        const previewUrl = file ? URL.createObjectURL(file) : null;
        return {
          ...prev,
          [name]: previewUrl
        };
      });
      
      setImages(prev => ({
        ...prev,
        [name]: file
      }));
    }
  };

  // Remove single image preview
  const handleRemoveImage = (fieldName) => {
    setImages(prev => ({ ...prev, [fieldName]: null }));
    setImagePreviews(prev => {
      if (prev[fieldName] && prev[fieldName].startsWith('blob:')) {
        URL.revokeObjectURL(prev[fieldName]);
      }
      return { ...prev, [fieldName]: null };
    });
    
    // Clear file input
    const fileInput = document.querySelector(`input[name="${fieldName}"]`);
    if (fileInput) fileInput.value = '';
  };

  const handleRemoveGalleryItem = (indexToRemove) => {
    const existingCount = Array.isArray(keptExistingGalleryImages) ? keptExistingGalleryImages.length : 0;

    if (indexToRemove < existingCount) {
      // Remove an already-saved media item from the "kept" list so backend deletes it on save.
      setKeptExistingGalleryImages((prev) => (prev || []).filter((_, i) => i !== indexToRemove));
    } else {
      // Remove a newly-added file from this editing session.
      const newIndex = indexToRemove - existingCount;
      setImages((prev) => ({
        ...prev,
        gallery_images: (prev.gallery_images || []).filter((_, i) => i !== newIndex),
      }));
    }

    setImagePreviews((prev) => {
      const next = [...(prev.gallery_images || [])];
      const removedPreview = next[indexToRemove];
      if (removedPreview && typeof removedPreview === 'string' && removedPreview.startsWith('blob:')) {
        URL.revokeObjectURL(removedPreview);
      }
      next.splice(indexToRemove, 1);
      return { ...prev, gallery_images: next };
    });
  };

  // Portfolio Items Management
  const addPortfolioItem = () => {
    setFormData(prev => ({
      ...prev,
      graphics_portfolio_items: [
        ...(prev.graphics_portfolio_items || []),
        { id: Date.now(), image: '', title: '', category: 'branding', type: 'final' }
      ]
    }));
  };

  const removePortfolioItem = (index) => {
    setFormData(prev => {
      const newItems = [...(prev.graphics_portfolio_items || [])];
      newItems.splice(index, 1);
      return { ...prev, graphics_portfolio_items: newItems };
    });
    
    // Remove image preview and file
    setImages(prev => {
      const newImages = [...(prev.graphics_portfolio_items || [])];
      newImages.splice(index, 1);
      return { ...prev, graphics_portfolio_items: newImages };
    });
    
    setImagePreviews(prev => {
      const newPreviews = [...(prev.graphics_portfolio_items || [])];
      if (newPreviews[index] && newPreviews[index].startsWith('blob:')) {
        URL.revokeObjectURL(newPreviews[index]);
      }
      newPreviews.splice(index, 1);
      return { ...prev, graphics_portfolio_items: newPreviews };
    });
  };

  const updatePortfolioItem = (index, field, value) => {
    setFormData(prev => {
      const newItems = [...(prev.graphics_portfolio_items || [])];
      newItems[index] = { ...newItems[index], [field]: value };
      return { ...prev, graphics_portfolio_items: newItems };
    });
  };

  const handlePortfolioItemImageChange = (index, e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (100MB max)
    const maxSize = 100 * 1024 * 1024;
    if (file.size > maxSize) {
      showError('File exceeds 100MB limit. Please select a smaller file.');
      e.target.value = '';
      return;
    }

    // Validate file type (images only)
    if (!isAllowedImageFile(file)) {
      showError('Invalid file type. Please select a valid image (e.g. JPEG, PNG, WebP, AVIF).');
      e.target.value = '';
      return;
    }

    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    
    // Update images and previews
    setImages(prev => {
      const newImages = [...(prev.graphics_portfolio_items || [])];
      newImages[index] = file;
      return { ...prev, graphics_portfolio_items: newImages };
    });
    
    setImagePreviews(prev => {
      const newPreviews = [...(prev.graphics_portfolio_items || [])];
      // Revoke old preview if it exists and is a blob URL
      if (newPreviews[index] && newPreviews[index].startsWith('blob:')) {
        URL.revokeObjectURL(newPreviews[index]);
      }
      newPreviews[index] = previewUrl;
      return { ...prev, graphics_portfolio_items: newPreviews };
    });
  };

  // Cleanup preview URLs when component unmounts
  useEffect(() => {
    return () => {
      // Revoke all preview URLs to prevent memory leaks (only blob URLs)
      if (imagePreviews.hero_image && typeof imagePreviews.hero_image === 'string' && imagePreviews.hero_image.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreviews.hero_image);
      }
      if (imagePreviews.section1_image && typeof imagePreviews.section1_image === 'string' && imagePreviews.section1_image.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreviews.section1_image);
      }
      if (imagePreviews.section2_image && typeof imagePreviews.section2_image === 'string' && imagePreviews.section2_image.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreviews.section2_image);
      }
      if (imagePreviews.gallery_images && imagePreviews.gallery_images.length > 0) {
        imagePreviews.gallery_images.forEach(url => {
          if (url && typeof url === 'string' && url.startsWith('blob:')) {
            URL.revokeObjectURL(url);
          }
        });
      }
      if (imagePreviews.graphics_wip_sketch_image && typeof imagePreviews.graphics_wip_sketch_image === 'string' && imagePreviews.graphics_wip_sketch_image.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreviews.graphics_wip_sketch_image);
      }
      if (imagePreviews.graphics_wip_final_image && typeof imagePreviews.graphics_wip_final_image === 'string' && imagePreviews.graphics_wip_final_image.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreviews.graphics_wip_final_image);
      }
      if (imagePreviews.graphics_portfolio_items && imagePreviews.graphics_portfolio_items.length > 0) {
        imagePreviews.graphics_portfolio_items.forEach(url => {
          if (url && typeof url === 'string' && url.startsWith('blob:')) {
            URL.revokeObjectURL(url);
          }
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleWhyChooseUsChange = (index, field, value) => {
    const updated = [...formData.why_choose_us];
    updated[index][field] = value;
    setFormData(prev => ({ ...prev, why_choose_us: updated }));
  };

  const addWhyChooseUsItem = () => {
    setFormData(prev => ({
      ...prev,
      why_choose_us: [...prev.why_choose_us, { title: '', description: '' }]
    }));
  };

  const removeWhyChooseUsItem = (index) => {
    setFormData(prev => ({
      ...prev,
      why_choose_us: prev.why_choose_us.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const formDataToSend = new FormData();
      // Always derive slug/route from name so user never has to deal with it
      const slug = generateSlug(formData.name) || formData.slug;
      const route = slug ? `/${slug}` : formData.route;
      const submitData = { ...formData, slug, route };

      // Normalize custom_sections for Photograph: ensure exactly 3 wedding_fashion blocks
      let customSectionsToSend = formData.custom_sections || [];
      if (slug === 'photograph' || formData.name?.toLowerCase().includes('photograph')) {
        const weddingItems = customSectionsToSend.filter(s => s.type === 'wedding_fashion');
        const others = customSectionsToSend.filter(s => s.type !== 'wedding_fashion');
        const threeBlocks = [0, 1, 2].map(i => weddingItems[i] || {
          type: 'wedding_fashion', title: '', content: '', images: ['']
        });
        customSectionsToSend = [...threeBlocks, ...others];
      }

      // Append all text fields (use submitData which has normalized slug/route)
      Object.keys(submitData).forEach(key => {
        // Never append file fields here — gallery/hero use separate `images` state + multer
        if (key === 'gallery_images') return;
        const value = submitData[key];
        // JSON fields
        if (
          key === 'why_choose_us' ||
          key === 'graphics_portfolio_categories' ||
          key === 'graphics_portfolio_items' ||
          key === 'graphics_typography_showcase' ||
          key === 'web_services' ||
          key === 'web_projects' ||
          key === 'web_service_details' ||
          key === 'pa_benefits' || key === 'pa_equipment_services' || key === 'pa_features' || key === 'pa_event_types' || key === 'pa_how_it_works' ||
          key === 'decoration_hero_images' ||
          key === 'decoration_pillars' || key === 'decoration_gallery_projects' || key === 'decoration_process_steps' || key === 'decoration_testimonial' || key === 'decoration_packages' ||
          key === 'video_editing_portfolio' || key === 'video_editing_workflow' || key === 'video_editing_before_after' || key === 'video_editing_tools' ||
          key === 'photo_editing_before_after' || key === 'photo_editing_portfolio' || key === 'photo_editing_services' || key === 'photo_editing_tools' ||
          key === 'djing_categories' || key === 'djing_gear' || key === 'djing_process' ||
          key === 'custom_sections'
        ) {
          const val = key === 'custom_sections' ? customSectionsToSend : value;
          formDataToSend.append(key, JSON.stringify(val || (key === 'web_service_details' ? [] : (typeof val === 'object' ? val : {}))));
        } else {
          formDataToSend.append(key, value);
        }
      });

      // Append images
      if (images.hero_image) {
        formDataToSend.append('hero_image', images.hero_image);
      }
      if (images.section1_image) {
        formDataToSend.append('section1_image', images.section1_image);
      }
      if (images.section2_image) {
        formDataToSend.append('section2_image', images.section2_image);
      }

      // Graphics Design WIP images
      if (images.graphics_wip_sketch_image) {
        formDataToSend.append('graphics_wip_sketch_image', images.graphics_wip_sketch_image);
      }
      if (images.graphics_wip_final_image) {
        formDataToSend.append('graphics_wip_final_image', images.graphics_wip_final_image);
      }

      // Graphics Design Portfolio Items images
      if (images.graphics_portfolio_items && images.graphics_portfolio_items.length > 0) {
        images.graphics_portfolio_items.forEach((file, index) => {
          if (file) {
            formDataToSend.append(`portfolio_item_${index}_image`, file);
          }
        });
      }

      // Web Design hero image
      if (images.web_hero_image) {
        formDataToSend.append('web_hero_image', images.web_hero_image);
      }

      // Web Design project images
      if (images.web_project_images) {
        images.web_project_images.forEach((file, index) => {
          if (file) {
            formDataToSend.append(`web_project_item_${index}_image`, file);
          }
        });
      }

      // Web Design detail images
      if (images.web_detail_images) {
        images.web_detail_images.forEach((file, index) => {
          if (file) {
            formDataToSend.append(`web_detail_block_${index}_image`, file);
          }
        });
      }

      // Handle gallery images: send new files + track kept existing ones
      if (images.gallery_images && images.gallery_images.length > 0) {
        console.log('Sending new gallery images:', images.gallery_images.length, 'files');
        images.gallery_images.forEach((file, index) => {
          console.log(`Gallery image ${index + 1}:`, file.name, file.size);
          formDataToSend.append('gallery_images', file);
        });
      }
      
      // Send the list of kept existing gallery images (for deletion tracking)
      // This tells the backend which existing images to keep
      if (isEditing) {
        formDataToSend.append('kept_gallery_images', JSON.stringify(keptExistingGalleryImages));
        console.log('Kept existing gallery images:', keptExistingGalleryImages.length);
      }

      // Video Editing: force JSON fields (multipart can drop or duplicate keys on some hosts).
      // FormData.set replaces a key so the API always receives portfolio/workflow/tools arrays.
      if (slug === 'video-editing' || route === '/video-editing') {
        formDataToSend.set(
          'video_editing_portfolio',
          JSON.stringify(submitData.video_editing_portfolio ?? [])
        );
        formDataToSend.set(
          'video_editing_workflow',
          JSON.stringify(submitData.video_editing_workflow ?? [])
        );
        formDataToSend.set(
          'video_editing_before_after',
          JSON.stringify(submitData.video_editing_before_after ?? [])
        );
        formDataToSend.set(
          'video_editing_tools',
          JSON.stringify(submitData.video_editing_tools ?? [])
        );
        formDataToSend.set('video_editing_hero_video', submitData.video_editing_hero_video ?? '');
        formDataToSend.set('video_editing_hero_title', submitData.video_editing_hero_title ?? '');
      }

      if (isEditing) {
        await axios.put(`/services/${id}`, formDataToSend);
        showSuccess('Service updated successfully!');
        
        // After updating, stay on the edit page and refresh the data
        // This allows the user to see their changes and continue editing if needed
        await fetchService();
        
        // Clear previews after successful submission (preserve web design arrays)
        setImagePreviews(prev => {
          if (prev.hero_image && prev.hero_image.startsWith('blob:')) URL.revokeObjectURL(prev.hero_image);
          if (prev.section1_image && prev.section1_image.startsWith('blob:')) URL.revokeObjectURL(prev.section1_image);
          if (prev.section2_image && prev.section2_image.startsWith('blob:')) URL.revokeObjectURL(prev.section2_image);
          if (prev.gallery_images && prev.gallery_images.length > 0) {
            prev.gallery_images.forEach(url => url && url.startsWith('blob:') && URL.revokeObjectURL(url));
          }
          return {
            ...prev,
            hero_image: null,
            section1_image: null,
            section2_image: null,
            gallery_images: []
          };
        });
        
        // Clear selected images (keep web design arrays so they stay defined)
        setImages(prev => ({
          ...prev,
          hero_image: null,
          section1_image: null,
          section2_image: null,
          gallery_images: [],
          web_hero_image: null,
          web_project_images: prev.web_project_images ?? Array(4).fill(null),
          web_detail_images: prev.web_detail_images ?? Array(6).fill(null)
        }));
      } else {
        await axios.post('/services', formDataToSend);
        showSuccess('Service created successfully!');
        
        // After creating, navigate to the services list
        navigate('/services');
      }
    } catch (err) {
      console.error('Error saving service:', err);
      const errorMsg = err.response?.data?.error || err.response?.data?.message || 'Failed to save service';
      setError(errorMsg);
      showError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="service-form-container">
        <div className="loading">Loading service data...</div>
      </div>
    );
  }

  return (
    <div className="service-form-container">
      <div className="page-header">
        <h1>{isEditing ? 'Edit Service' : 'Add New Service'}</h1>
        <button onClick={() => navigate('/services')} className="btn btn-secondary btn-back">
          <ArrowLeft size={20} />
          Back to Services
        </button>
      </div>

      {error && (
        <div className="alert alert-danger">{error}</div>
      )}

      <form onSubmit={handleSubmit} className="service-form">
        
        {/* BASIC INFO SECTION */}
        <div className="form-section">
          <h2>Basic Information</h2>
          
          <div className="form-group">
            <label htmlFor="service_number">Service Number *</label>
            <input
              type="text"
              className="form-control"
              id="service_number"
              name="service_number"
              value={formData.service_number}
              onChange={handleChange}
              placeholder="e.g., 01, 02, 03"
              maxLength="10"
              required
            />
            <small className="form-text">Display number (e.g., 01, 02, 03)</small>
          </div>

          <div className="form-group">
            <label htmlFor="name">Service Name *</label>
            <input
              type="text"
              className="form-control"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., Photography, Videography"
              required
            />
            <small className="form-text">URL will be auto-generated (e.g., /photograph)</small>
          </div>

          <div className="form-group">
            <label htmlFor="description">Short Description *</label>
            <textarea
              className="form-control"
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="2"
              placeholder="Brief description for cards..."
              required
            />
          </div>
        </div>

        {/* HERO SECTION - Hidden for Graphics, Web Design, and Event Decoration (hero = carousel + mosaic in page section) */}
        {!isGraphicsDesignService && !isWebDesignService && !isEventDecorationService && !isVideoEditingService && (
          <div className="form-section">
            <h2>Hero Section (Top of Page)</h2>

            <div className="form-group">
              <label htmlFor="hero_image">
                {isVideographService ? 'Hero Video/Image' : 'Hero Image'}
              </label>
              <input
                type="file"
                className="form-control"
                id="hero_image"
                name="hero_image"
                onChange={handleImageChange}
                accept={isVideographService ? "image/*,video/*" : "image/*"}
              />
              {isVideographService && (
                <small className="form-text">For videograph: You can upload a video or image for the hero section</small>
              )}
              {imagePreviews.hero_image && (
                <div className="image-preview-container">
                  <div className="image-preview-wrapper">
                    {(() => {
                      const isNewVideo = imagePreviews.hero_image.startsWith('blob:') && images.hero_image?.type?.startsWith('video/');
                      const isExistingVideo = !imagePreviews.hero_image.startsWith('blob:') && (
                        imagePreviews.hero_image.endsWith('.mp4') || 
                        imagePreviews.hero_image.endsWith('.webm') || 
                        imagePreviews.hero_image.endsWith('.mov') || 
                        imagePreviews.hero_image.endsWith('.avi')
                      );
                      
                      if (isNewVideo || isExistingVideo) {
                        return (
                          <video 
                            src={imagePreviews.hero_image} 
                            className="image-preview-img" 
                            controls 
                            style={{ maxWidth: '400px', maxHeight: '300px' }}
                          />
                        );
                      } else {
                        return (
                          <img src={imagePreviews.hero_image} alt="Hero preview" className="image-preview-img" />
                        );
                      }
                    })()}
                    <button
                      type="button"
                      onClick={() => handleRemoveImage('hero_image')}
                      className="remove-image-btn"
                      title="Remove file"
                    >
                      ✕
                    </button>
                  </div>
                  {images.hero_image && (
                    <p className="image-filename">{images.hero_image.name}</p>
                  )}
                  <small className="image-preview-label">
                    {images.hero_image 
                      ? (images.hero_image.type?.startsWith('video/') ? 'New video preview' : 'New image preview')
                      : (existingImages.hero_image?.endsWith('.mp4') || existingImages.hero_image?.endsWith('.webm') || existingImages.hero_image?.endsWith('.avi') ? 'Current video' : 'Current image')
                    }
                  </small>
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="hero_title">Hero Title</label>
              <input
                type="text"
                className="form-control"
                id="hero_title"
                name="hero_title"
                value={formData.hero_title}
                onChange={handleChange}
                placeholder="CAPTURING THE MAGIC OF YOUR SPECIAL DAY"
              />
            </div>

          </div>
        )}

        {/* HERO TITLE ONLY - For Graphics Design */}
        {isGraphicsDesignService && (
          <div className="form-section">
            <h2>Hero Title (Main Headline)</h2>
            <div className="form-group">
              <label htmlFor="hero_title">Hero Title</label>
              <input
                type="text"
                className="form-control"
                id="hero_title"
                name="hero_title"
                value={formData.hero_title}
                onChange={handleChange}
                placeholder="IDEAS RENDERED IN PIXELS & INK"
              />
              <small className="form-text">This is the large headline text at the top of the Graphics Design page</small>
            </div>
          </div>
        )}

        {/* CONTENT SECTION 1 - Hidden for Graphics, Web Design, and Event Decoration (Event Decoration uses dedicated block below) */}
        {!isGraphicsDesignService && !isWebDesignService && !isEventDecorationService && !isVideoEditingService && (
          <div className="form-section">
            <h2>Content Section 1</h2>

          <div className="form-group">
            <label htmlFor="section1_title">Section 1 Title</label>
            <input
              type="text"
              className="form-control"
              id="section1_title"
              name="section1_title"
              value={formData.section1_title}
              onChange={handleChange}
              placeholder="Immortalizing Moments One Click at a Time"
            />
          </div>

          <div className="form-group">
            <label htmlFor="section1_content">Section 1 Content</label>
            <textarea
              className="form-control form-control-content"
              id="section1_content"
              name="section1_content"
              value={formData.section1_content}
              onChange={handleChange}
              rows="12"
              placeholder="Detailed content for first section..."
            />
          </div>

          <div className="form-group">
            <label htmlFor="section1_image">Section 1 Image</label>
            <input
              type="file"
              className="form-control"
              id="section1_image"
              name="section1_image"
              onChange={handleImageChange}
              accept="image/*"
            />
            {imagePreviews.section1_image && (
              <div className="image-preview-container">
                <div className="image-preview-wrapper">
                  <img src={imagePreviews.section1_image} alt="Section 1 preview" className="image-preview-img" />
                  <button
                    type="button"
                    onClick={() => handleRemoveImage('section1_image')}
                    className="remove-image-btn"
                    title="Remove image"
                  >
                    ✕
                  </button>
                </div>
                {images.section1_image && (
                  <p className="image-filename">{images.section1_image.name}</p>
                )}
                <small className="image-preview-label">
                  {images.section1_image ? 'New image preview' : 'Current image'}
                </small>
              </div>
            )}
          </div>
          </div>
        )}

        {/* CONTENT SECTION 2 - Hidden for Graphics, Web Design, and Event Decoration */}
        {!isGraphicsDesignService && !isWebDesignService && !isEventDecorationService && !isVideoEditingService && (
          <div className="form-section">
            <h2>Content Section 2</h2>

          <div className="form-group">
            <label htmlFor="section2_title">Section 2 Title</label>
            <input
              type="text"
              className="form-control"
              id="section2_title"
              name="section2_title"
              value={formData.section2_title}
              onChange={handleChange}
              placeholder="Let's Create Timeless Memories Together"
            />
          </div>

          <div className="form-group">
            <label htmlFor="section2_content">Section 2 Content</label>
            <textarea
              className="form-control form-control-content"
              id="section2_content"
              name="section2_content"
              value={formData.section2_content}
              onChange={handleChange}
              rows="12"
              placeholder="Detailed content for second section..."
            />
          </div>

          <div className="form-group">
            <label htmlFor="cta_title">CTA Title (Call-to-Action heading)</label>
            <input
              type="text"
              className="form-control"
              id="cta_title"
              name="cta_title"
              value={formData.cta_title}
              onChange={handleChange}
              placeholder="Ready to Book?"
            />
          </div>
          <div className="form-group">
            <label htmlFor="cta_subtitle">CTA Subtitle (paragraph under CTA title)</label>
            <textarea
              className="form-control"
              id="cta_subtitle"
              name="cta_subtitle"
              value={formData.cta_subtitle}
              onChange={handleChange}
              rows="2"
              placeholder="Get in touch today and secure your rental!"
            />
          </div>

          <div className="form-group">
            <label htmlFor="section2_image">Section 2 Image</label>
            <input
              type="file"
              className="form-control"
              id="section2_image"
              name="section2_image"
              onChange={handleImageChange}
              accept="image/*"
            />
            {imagePreviews.section2_image && (
              <div className="image-preview-container">
                <div className="image-preview-wrapper">
                  <img src={imagePreviews.section2_image} alt="Section 2 preview" className="image-preview-img" />
                  <button
                    type="button"
                    onClick={() => handleRemoveImage('section2_image')}
                    className="remove-image-btn"
                    title="Remove image"
                  >
                    ✕
                  </button>
                </div>
                {images.section2_image && (
                  <p className="image-filename">{images.section2_image.name}</p>
                )}
                <small className="image-preview-label">
                  {images.section2_image ? 'New image preview' : 'Current image'}
                </small>
              </div>
            )}
          </div>
          </div>
        )}

        {/* WEB DESIGN LAYOUT - Editable content for /web-design page */}
        {isWebDesignService && (
          <div className="form-section web-design-section">
            <div className="section-header-with-description">
              <h2>Web Design Page Content</h2>
              <p className="section-description">Manage the hero, service cards, projects, and detailed service blocks for the Web Design page.</p>
            </div>

            {/* Hero Section Card */}
            <div className="admin-content-card">
              <div className="card-header">
                <h3>Hero Section</h3>
              </div>
              <div className="card-body">
                <div className="form-group">
                  <label>Hero Title</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.web_hero_title || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, web_hero_title: e.target.value }))}
                    placeholder="INNOVATIVE DESIGN SOLUTIONS FOR YOUR BUSINESS"
                  />
                </div>
                <div className="form-group">
                  <label>Hero Background Image</label>
                  <div className="custom-file-upload">
                    <input
                      type="file"
                      id="web_hero_image_input"
                      className="file-input-hidden"
                      name="web_hero_image"
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                    <label htmlFor="web_hero_image_input" className="file-upload-label">
                      {images.web_hero_image ? 'Change Image' : 'Select Hero Image'}
                    </label>
                  </div>
                  <ImagePreview
                    preview={imagePreviews.web_hero_image}
                    label="Hero background"
                    onRemove={() => handleRemoveImage('web_hero_image')}
                    fileName={images.web_hero_image?.name}
                    isNew={!!images.web_hero_image}
                  />
                </div>
              </div>
            </div>

            {/* Services Section Card */}
            <div className="admin-content-card">
              <div className="card-header">
                <h3>Services Section (Top Cards)</h3>
              </div>
              <div className="card-body">
                <div className="form-row">
                  <div className="form-group col-md-6">
                    <label>Section Subtitle</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.web_services?.sectionSubtitle || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        web_services: { ...(prev.web_services || {}), sectionSubtitle: e.target.value }
                      }))}
                      placeholder="|| Our Service"
                    />
                  </div>
                  <div className="form-group col-md-6">
                    <label>Section Title</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.web_services?.sectionTitle || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        web_services: { ...(prev.web_services || {}), sectionTitle: e.target.value }
                      }))}
                      placeholder="Services We Provide For Our Clients"
                    />
                  </div>
                </div>

                <div className="items-grid">
                  {(formData.web_services?.cards || []).map((card, idx) => (
                    <div key={idx} className="nested-item-card">
                      <div className="nested-card-header">
                        <h4>Card {idx + 1}</h4>
                      </div>
                      <div className="form-group">
                        <label>Title</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Title"
                          value={card.title || ''}
                          onChange={(e) => {
                            const cards = [...(formData.web_services?.cards || [])];
                            cards[idx] = { ...card, title: e.target.value };
                            setFormData(prev => ({ ...prev, web_services: { ...prev.web_services, cards } }));
                          }}
                        />
                      </div>
                      <div className="form-group">
                        <label>Icon Style</label>
                        <select
                          className="form-control"
                          value={card.icon || 'dev'}
                          onChange={(e) => {
                            const cards = [...(formData.web_services?.cards || [])];
                            cards[idx] = { ...card, icon: e.target.value };
                            setFormData(prev => ({ ...prev, web_services: { ...prev.web_services, cards } }));
                          }}
                        >
                          <option value="dev">Development</option>
                          <option value="ui">UI/UX Design</option>
                          <option value="illus">Illustration</option>
                          <option value="seo">SEO Marketing</option>
                          <option value="brand">Brand Identity</option>
                          <option value="analysis">Data Analysis</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Skills (one per line)</label>
                        <AutoResizeTextarea
                          className="form-control"
                          minHeight={80}
                          maxHeight={280}
                          placeholder="Skills..."
                          value={(card.skills || []).join('\n')}
                          onChange={(e) => {
                            const skills = e.target.value.split('\n').map(s => s.trim());
                            const cards = [...(formData.web_services?.cards || [])];
                            cards[idx] = { ...card, skills };
                            setFormData(prev => ({ ...prev, web_services: { ...prev.web_services, cards } }));
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Projects Section Card */}
            <div className="admin-content-card">
              <div className="card-header">
                <h3>Projects Section (Slider)</h3>
              </div>
              <div className="card-body">
                <div className="form-row">
                  <div className="form-group col-md-6">
                    <label>Section Subtitle</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.web_projects?.sectionSubtitle || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        web_projects: { ...(prev.web_projects || {}), sectionSubtitle: e.target.value }
                      }))}
                      placeholder="|| Awesome Works"
                    />
                  </div>
                  <div className="form-group col-md-6">
                    <label>Section Title</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.web_projects?.sectionTitle || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        web_projects: { ...(prev.web_projects || {}), sectionTitle: e.target.value }
                      }))}
                      placeholder="Our Complete Projects"
                    />
                  </div>
                </div>

                <div className="items-grid">
                  {(formData.web_projects?.items || []).map((item, idx) => (
                    <div key={idx} className="nested-item-card">
                      <div className="nested-card-header">
                        <h4>Project {idx + 1}</h4>
                      </div>
                      <div className="form-group">
                        <label>Project Image</label>
                        <div className="custom-file-upload">
                          <input
                            type="file"
                            id={`project_image_input_${idx}`}
                            className="file-input-hidden"
                            name={`web_project_item_${idx}_image`}
                            accept="image/*"
                            onChange={handleImageChange}
                          />
                          <label htmlFor={`project_image_input_${idx}`} className="file-upload-label">
                            {images.web_project_images?.[idx] ? 'Change' : 'Select Image'}
                          </label>
                        </div>
                        <ImagePreview
                          preview={imagePreviews.web_project_images?.[idx]}
                          label={`Project ${idx + 1}`}
                          onRemove={() => {
                            setImages(prev => {
                              const newImgs = [...(prev.web_project_images || Array(4).fill(null))];
                              newImgs[idx] = null;
                              return { ...prev, web_project_images: newImgs };
                            });
                            setImagePreviews(prev => {
                              const newPreviews = [...(prev.web_project_images || Array(4).fill(null))];
                              if (newPreviews[idx] && newPreviews[idx].startsWith('blob:')) URL.revokeObjectURL(newPreviews[idx]);
                              newPreviews[idx] = null;
                              return { ...prev, web_project_images: newPreviews };
                            });
                            const items = [...(formData.web_projects?.items || [])];
                            if (items[idx]) items[idx] = { ...items[idx], image: '' };
                            setFormData(prev => ({ ...prev, web_projects: { ...prev.web_projects, items } }));
                          }}
                          fileName={images.web_project_images?.[idx]?.name}
                          isNew={!!images.web_project_images?.[idx]}
                        />
                      </div>
                      <div className="form-group">
                        <label>Title</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Title"
                          value={item.title || ''}
                          onChange={(e) => {
                            const items = [...(formData.web_projects?.items || [])];
                            items[idx] = { ...item, title: e.target.value };
                            setFormData(prev => ({ ...prev, web_projects: { ...prev.web_projects, items } }));
                          }}
                        />
                      </div>
                      <div className="form-group">
                        <label>Subtitle / Technologies</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Subtitle"
                          value={item.subtitle || ''}
                          onChange={(e) => {
                            const items = [...(formData.web_projects?.items || [])];
                            items[idx] = { ...item, subtitle: e.target.value };
                            setFormData(prev => ({ ...prev, web_projects: { ...prev.web_projects, items } }));
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Service Details Section Card */}
            <div className="admin-content-card">
              <div className="card-header">
                <h3>Detailed Service Blocks (Bottom Content)</h3>
              </div>
              <div className="card-body">
                <p className="card-description">These are the 6 detailed sections at the bottom of the Web Design page.</p>
                <div className="details-stack">
                  {((formData.web_service_details && formData.web_service_details.length > 0) ? formData.web_service_details : defaultWebServiceDetails).map((block, idx) => (
                    <div key={idx} className="nested-item-card full-width">
                      <div className="nested-card-header">
                        <h4>Service Detail Block {idx + 1}: {block.category}</h4>
                      </div>
                      <div className="form-row">
                        <div className="form-group col-md-4">
                          <label>Category</label>
                          <input
                            type="text"
                            className="form-control"
                            value={block.category || ''}
                            onChange={(e) => {
                              const blocks = [...(formData.web_service_details?.length ? formData.web_service_details : defaultWebServiceDetails)];
                              blocks[idx] = { ...blocks[idx], category: e.target.value };
                              setFormData(prev => ({ ...prev, web_service_details: blocks }));
                            }}
                          />
                        </div>
                        <div className="form-group col-md-4">
                          <label>Subtitle</label>
                          <input
                            type="text"
                            className="form-control"
                            value={block.subtitle || ''}
                            onChange={(e) => {
                              const blocks = [...(formData.web_service_details?.length ? formData.web_service_details : defaultWebServiceDetails)];
                              blocks[idx] = { ...blocks[idx], subtitle: e.target.value };
                              setFormData(prev => ({ ...prev, web_service_details: blocks }));
                            }}
                          />
                        </div>
                        <div className="form-group col-md-4">
                          <label>Main Title</label>
                          <input
                            type="text"
                            className="form-control"
                            value={block.title || ''}
                            onChange={(e) => {
                              const blocks = [...(formData.web_service_details?.length ? formData.web_service_details : defaultWebServiceDetails)];
                              blocks[idx] = { ...blocks[idx], title: e.target.value };
                              setFormData(prev => ({ ...prev, web_service_details: blocks }));
                            }}
                          />
                        </div>
                      </div>
                      
                      <div className="form-row">
                        <div className="form-group col-md-6">
                          <label>Service Image</label>
                          <div className="custom-file-upload">
                            <input
                              type="file"
                              id={`detail_image_input_${idx}`}
                              className="file-input-hidden"
                              name={`web_detail_block_${idx}_image`}
                              accept="image/*"
                              onChange={handleImageChange}
                            />
                            <label htmlFor={`detail_image_input_${idx}`} className="file-upload-label">
                              {images.web_detail_images?.[idx] ? 'Change Image' : 'Select Image'}
                            </label>
                          </div>
                          <ImagePreview
                            preview={imagePreviews.web_detail_images?.[idx]}
                            label={`Detail ${idx + 1}`}
                            onRemove={() => {
                              setImages(prev => {
                                const newImgs = [...(prev.web_detail_images || Array(6).fill(null))];
                                newImgs[idx] = null;
                                return { ...prev, web_detail_images: newImgs };
                              });
                              setImagePreviews(prev => {
                                const newPreviews = [...(prev.web_detail_images || Array(6).fill(null))];
                                if (newPreviews[idx] && newPreviews[idx].startsWith('blob:')) URL.revokeObjectURL(newPreviews[idx]);
                                newPreviews[idx] = null;
                                return { ...prev, web_detail_images: newPreviews };
                              });
                              const blocks = [...(formData.web_service_details?.length ? formData.web_service_details : defaultWebServiceDetails)];
                              if (blocks[idx]) blocks[idx] = { ...blocks[idx], imageUrl: '' };
                              setFormData(prev => ({ ...prev, web_service_details: blocks }));
                            }}
                            fileName={images.web_detail_images?.[idx]?.name}
                            isNew={!!images.web_detail_images?.[idx]}
                          />
                        </div>
                        <div className="form-group col-md-6">
                          <label>Details / List Items (one per line)</label>
                          <AutoResizeTextarea
                            className="form-control"
                            minHeight={100}
                            maxHeight={320}
                            value={(block.details || []).join('\n')}
                            onChange={(e) => {
                              const details = e.target.value.split('\n').map(s => s.trim());
                              const blocks = [...(formData.web_service_details?.length ? formData.web_service_details : defaultWebServiceDetails)];
                              blocks[idx] = { ...blocks[idx], details };
                              setFormData(prev => ({ ...prev, web_service_details: blocks }));
                            }}
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label>Main Content Paragraphs (one paragraph per line)</label>
                        <AutoResizeTextarea
                          className="form-control"
                          minHeight={120}
                          maxHeight={500}
                          value={(block.content || []).join('\n\n')}
                          onChange={(e) => {
                            const content = e.target.value.split(/\n\n+/).map(s => s.trim());
                            const blocks = [...(formData.web_service_details?.length ? formData.web_service_details : defaultWebServiceDetails)];
                            blocks[idx] = { ...blocks[idx], content };
                            setFormData(prev => ({ ...prev, web_service_details: blocks }));
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* PA SYSTEM RENTAL SPECIFIC SECTION */}
        {(formData.slug === 'pa-system-rental' || formData.route === '/pa-system-rental') && (
          <div className="form-section pa-system-section">
            <h2>PA System Page Content</h2>
            <p className="text-muted">Edit the content shown on the PA System Rental frontend page. Section titles use the generic fields above (Section 1 Title, Section 1 Content, Section 2 Title, Section 2 Content, CTA Title).</p>
            <div className="form-group">
              <label>Hero Video URL (optional, leave blank to use default)</label>
              <input type="text" className="form-control" value={formData.pa_hero_video || ''} onChange={(e) => setFormData(prev => ({ ...prev, pa_hero_video: e.target.value }))} placeholder="/uploads/services/hero-video.mp4" />
            </div>
            <div className="form-group">
              <label>Hero Title</label>
              <input type="text" className="form-control" value={formData.pa_hero_title || ''} onChange={(e) => setFormData(prev => ({ ...prev, pa_hero_title: e.target.value }))} placeholder="PA System Rental Services" />
            </div>
            <div className="form-group">
              <label>Benefits (4 items: title + description)</label>
              {(formData.pa_benefits || []).map((b, i) => (
                <div key={i} className="border rounded p-3 mb-2">
                  <input type="text" className="form-control mb-2" value={b.title || ''} onChange={(e) => { const arr = [...(formData.pa_benefits || [])]; arr[i] = { ...arr[i], title: e.target.value }; setFormData(prev => ({ ...prev, pa_benefits: arr })); }} placeholder="Title" />
                  <input type="text" className="form-control" value={b.description || ''} onChange={(e) => { const arr = [...(formData.pa_benefits || [])]; arr[i] = { ...arr[i], description: e.target.value }; setFormData(prev => ({ ...prev, pa_benefits: arr })); }} placeholder="Description" />
                </div>
              ))}
            </div>
            <div className="form-group">
              <label>Equipment Services (6 items: title, description, optional img URL)</label>
              <button type="button" className="btn btn-sm btn-secondary mb-2" onClick={() => setFormData(prev => ({ ...prev, pa_equipment_services: [...(prev.pa_equipment_services || []), { title: '', description: '', img: '' }] }))}>+ Add</button>
              {(formData.pa_equipment_services || []).map((s, i) => (
                <div key={i} className="border rounded p-3 mb-2">
                  <div className="d-flex justify-content-between"><strong>Item {i + 1}</strong><button type="button" className="btn btn-sm btn-danger" onClick={() => setFormData(prev => ({ ...prev, pa_equipment_services: prev.pa_equipment_services.filter((_, j) => j !== i) }))}>Remove</button></div>
                  <input type="text" className="form-control mb-2" value={s.title || ''} onChange={(e) => { const arr = [...(formData.pa_equipment_services || [])]; arr[i] = { ...arr[i], title: e.target.value }; setFormData(prev => ({ ...prev, pa_equipment_services: arr })); }} placeholder="Title" />
                  <input type="text" className="form-control mb-2" value={s.description || ''} onChange={(e) => { const arr = [...(formData.pa_equipment_services || [])]; arr[i] = { ...arr[i], description: e.target.value }; setFormData(prev => ({ ...prev, pa_equipment_services: arr })); }} placeholder="Description" />
                  <input type="text" className="form-control form-control-sm" value={s.img || ''} onChange={(e) => { const arr = [...(formData.pa_equipment_services || [])]; arr[i] = { ...arr[i], img: e.target.value }; setFormData(prev => ({ ...prev, pa_equipment_services: arr })); }} placeholder="Image URL (optional)" />
                </div>
              ))}
            </div>
            <div className="form-group">
              <label>Features (4 items: title + items array)</label>
              {(formData.pa_features || []).map((f, i) => (
                <div key={i} className="border rounded p-3 mb-2">
                  <input type="text" className="form-control mb-2" value={f.title || ''} onChange={(e) => { const arr = [...(formData.pa_features || [])]; arr[i] = { ...arr[i], title: e.target.value }; setFormData(prev => ({ ...prev, pa_features: arr })); }} placeholder="Feature title" />
                  <input type="text" className="form-control" value={Array.isArray(f.items) ? f.items.join(', ') : ''} onChange={(e) => { const arr = [...(formData.pa_features || [])]; arr[i] = { ...arr[i], items: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }; setFormData(prev => ({ ...prev, pa_features: arr })); }} placeholder="Items (comma-separated)" />
                </div>
              ))}
            </div>
            <div className="form-group">
              <label>Event Types (6 items: title, optional img URL)</label>
              <button type="button" className="btn btn-sm btn-secondary mb-2" onClick={() => setFormData(prev => ({ ...prev, pa_event_types: [...(prev.pa_event_types || []), { title: '', img: '' }] }))}>+ Add</button>
              {(formData.pa_event_types || []).map((e, i) => (
                <div key={i} className="border rounded p-3 mb-2">
                  <div className="d-flex justify-content-between"><strong>Event {i + 1}</strong><button type="button" className="btn btn-sm btn-danger" onClick={() => setFormData(prev => ({ ...prev, pa_event_types: prev.pa_event_types.filter((_, j) => j !== i) }))}>Remove</button></div>
                  <input type="text" className="form-control mb-2" value={e.title || ''} onChange={(ev) => { const arr = [...(formData.pa_event_types || [])]; arr[i] = { ...arr[i], title: ev.target.value }; setFormData(prev => ({ ...prev, pa_event_types: arr })); }} placeholder="Title" />
                  <input type="text" className="form-control form-control-sm" value={e.img || ''} onChange={(ev) => { const arr = [...(formData.pa_event_types || [])]; arr[i] = { ...arr[i], img: ev.target.value }; setFormData(prev => ({ ...prev, pa_event_types: arr })); }} placeholder="Image URL (optional)" />
                </div>
              ))}
            </div>
            <div className="form-group">
              <label>How It Works (4 steps: icon, title, desc)</label>
              {(formData.pa_how_it_works || []).map((s, i) => (
                <div key={i} className="border rounded p-3 mb-2">
                  <input type="text" className="form-control mb-2" value={s.icon || ''} onChange={(e) => { const arr = [...(formData.pa_how_it_works || [])]; arr[i] = { ...arr[i], icon: e.target.value }; setFormData(prev => ({ ...prev, pa_how_it_works: arr })); }} placeholder="Icon (emoji e.g. 🔍)" />
                  <input type="text" className="form-control mb-2" value={s.title || ''} onChange={(e) => { const arr = [...(formData.pa_how_it_works || [])]; arr[i] = { ...arr[i], title: e.target.value }; setFormData(prev => ({ ...prev, pa_how_it_works: arr })); }} placeholder="Title" />
                  <input type="text" className="form-control" value={s.desc || ''} onChange={(e) => { const arr = [...(formData.pa_how_it_works || [])]; arr[i] = { ...arr[i], desc: e.target.value }; setFormData(prev => ({ ...prev, pa_how_it_works: arr })); }} placeholder="Description" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* EVENT DECORATION, VIDEO EDITING, PHOTO EDITING, DJING - JSON edit note */}
        {(formData.slug === 'event-decoration' || formData.route === '/event-decoration') && (
          <div className="form-section">
            <h2>Event Decoration Page Content</h2>
            <p className="text-muted small mb-3">
              Fields are ordered to match the public page top to bottom: hero, pillars, gallery, process, testimonial, packages, then bottom CTA.
            </p>

            <h3 className="h5 mb-3">Hero (top of page)</h3>
            <div className="form-group">
              <label>Headline (use \n for line breaks)</label>
              <input type="text" className="form-control" value={formData.decoration_hero_title || ''} onChange={(e) => setFormData(prev => ({ ...prev, decoration_hero_title: e.target.value }))} placeholder="TRANSFORMING SPACES, CRAFTING MOMENTS" />
            </div>

            <div className="form-group">
              <label>Left — carousel (slide order)</label>
              <small className="form-text text-muted d-block mb-2">
                Large hero slider images. If you leave this empty, the public site uses default bundled images. Upload replaces or set URL manually.
              </small>
              <button
                type="button"
                className="btn btn-sm btn-secondary mb-2"
                onClick={() => setFormData((prev) => {
                  const d = { ...(prev.decoration_hero_images || { carousel: [], mosaic: [] }) };
                  return { ...prev, decoration_hero_images: { ...d, carousel: [...(d.carousel || []), ''] } };
                })}
              >
                + Add carousel slide
              </button>
              {(formData.decoration_hero_images?.carousel || []).map((url, i) => (
                <div key={`car-${i}`} className="border rounded p-2 mb-2 d-flex flex-wrap align-items-center gap-2">
                  <span className="text-muted small">Slide {i + 1}</span>
                  {url ? (
                    <img
                      src={url.startsWith('http') ? url : `${getApiBaseUrl()}${url.startsWith('/') ? url : `/${url}`}`}
                      alt=""
                      style={{ width: 72, height: 48, objectFit: 'cover', borderRadius: 4 }}
                    />
                  ) : null}
                  <input
                    type="text"
                    className="form-control form-control-sm flex-grow-1"
                    style={{ minWidth: '200px' }}
                    value={url || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData((prev) => {
                        const d = { ...(prev.decoration_hero_images || { carousel: [], mosaic: [] }) };
                        const arr = [...(d.carousel || [])];
                        arr[i] = val;
                        return { ...prev, decoration_hero_images: { ...d, carousel: arr } };
                      });
                    }}
                    placeholder="/uploads/services/…"
                  />
                  <label className="btn btn-sm btn-outline-primary mb-0">
                    Upload
                    <input
                      type="file"
                      accept="image/*"
                      className="d-none"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        e.target.value = '';
                        if (!file) return;
                        try {
                          const { url: uploaded } = await servicesAPI.uploadImage(file);
                          setFormData((prev) => {
                            const d = { ...(prev.decoration_hero_images || { carousel: [], mosaic: [] }) };
                            const arr = [...(d.carousel || [])];
                            while (arr.length <= i) arr.push('');
                            arr[i] = uploaded;
                            return { ...prev, decoration_hero_images: { ...d, carousel: arr } };
                          });
                          showSuccess('Carousel image uploaded');
                        } catch (err) {
                          showError(err.response?.data?.error || 'Upload failed');
                        }
                      }}
                    />
                  </label>
                  <button type="button" className="btn btn-sm btn-outline-secondary" disabled={i === 0} onClick={() => {
                    setFormData((prev) => {
                      const d = { ...(prev.decoration_hero_images || { carousel: [], mosaic: [] }) };
                      const arr = [...(d.carousel || [])];
                      if (i <= 0) return prev;
                      [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]];
                      return { ...prev, decoration_hero_images: { ...d, carousel: arr } };
                    });
                  }}
                  >
                    ↑
                  </button>
                  <button type="button" className="btn btn-sm btn-outline-secondary" disabled={i >= (formData.decoration_hero_images?.carousel || []).length - 1} onClick={() => {
                    setFormData((prev) => {
                      const d = { ...(prev.decoration_hero_images || { carousel: [], mosaic: [] }) };
                      const arr = [...(d.carousel || [])];
                      if (i >= arr.length - 1) return prev;
                      [arr[i], arr[i + 1]] = [arr[i + 1], arr[i]];
                      return { ...prev, decoration_hero_images: { ...d, carousel: arr } };
                    });
                  }}
                  >
                    ↓
                  </button>
                  <button type="button" className="btn btn-sm btn-danger" onClick={() => {
                    setFormData((prev) => {
                      const d = { ...(prev.decoration_hero_images || { carousel: [], mosaic: [] }) };
                      const arr = [...(d.carousel || [])].filter((_, j) => j !== i);
                      return { ...prev, decoration_hero_images: { ...d, carousel: arr } };
                    });
                  }}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>

            <div className="form-group">
              <label>Right — mosaic (3×3 tiles, order preserved before shuffle)</label>
              <small className="form-text text-muted d-block mb-2">
                Nine (or fewer) images for the small grid; the site shuffles them automatically. If empty, bundled defaults are used.
              </small>
              <button
                type="button"
                className="btn btn-sm btn-secondary mb-2"
                onClick={() => setFormData((prev) => {
                  const d = { ...(prev.decoration_hero_images || { carousel: [], mosaic: [] }) };
                  return { ...prev, decoration_hero_images: { ...d, mosaic: [...(d.mosaic || []), ''] } };
                })}
              >
                + Add mosaic image
              </button>
              {(formData.decoration_hero_images?.mosaic || []).map((url, i) => (
                <div key={`mos-${i}`} className="border rounded p-2 mb-2 d-flex flex-wrap align-items-center gap-2">
                  <span className="text-muted small">Tile {i + 1}</span>
                  {url ? (
                    <img
                      src={url.startsWith('http') ? url : `${getApiBaseUrl()}${url.startsWith('/') ? url : `/${url}`}`}
                      alt=""
                      style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 4 }}
                    />
                  ) : null}
                  <input
                    type="text"
                    className="form-control form-control-sm flex-grow-1"
                    style={{ minWidth: '200px' }}
                    value={url || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData((prev) => {
                        const d = { ...(prev.decoration_hero_images || { carousel: [], mosaic: [] }) };
                        const arr = [...(d.mosaic || [])];
                        arr[i] = val;
                        return { ...prev, decoration_hero_images: { ...d, mosaic: arr } };
                      });
                    }}
                    placeholder="/uploads/services/…"
                  />
                  <label className="btn btn-sm btn-outline-primary mb-0">
                    Upload
                    <input
                      type="file"
                      accept="image/*"
                      className="d-none"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        e.target.value = '';
                        if (!file) return;
                        try {
                          const { url: uploaded } = await servicesAPI.uploadImage(file);
                          setFormData((prev) => {
                            const d = { ...(prev.decoration_hero_images || { carousel: [], mosaic: [] }) };
                            const arr = [...(d.mosaic || [])];
                            while (arr.length <= i) arr.push('');
                            arr[i] = uploaded;
                            return { ...prev, decoration_hero_images: { ...d, mosaic: arr } };
                          });
                          showSuccess('Mosaic image uploaded');
                        } catch (err) {
                          showError(err.response?.data?.error || 'Upload failed');
                        }
                      }}
                    />
                  </label>
                  <button type="button" className="btn btn-sm btn-outline-secondary" disabled={i === 0} onClick={() => {
                    setFormData((prev) => {
                      const d = { ...(prev.decoration_hero_images || { carousel: [], mosaic: [] }) };
                      const arr = [...(d.mosaic || [])];
                      if (i <= 0) return prev;
                      [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]];
                      return { ...prev, decoration_hero_images: { ...d, mosaic: arr } };
                    });
                  }}
                  >
                    ↑
                  </button>
                  <button type="button" className="btn btn-sm btn-outline-secondary" disabled={i >= (formData.decoration_hero_images?.mosaic || []).length - 1} onClick={() => {
                    setFormData((prev) => {
                      const d = { ...(prev.decoration_hero_images || { carousel: [], mosaic: [] }) };
                      const arr = [...(d.mosaic || [])];
                      if (i >= arr.length - 1) return prev;
                      [arr[i], arr[i + 1]] = [arr[i + 1], arr[i]];
                      return { ...prev, decoration_hero_images: { ...d, mosaic: arr } };
                    });
                  }}
                  >
                    ↓
                  </button>
                  <button type="button" className="btn btn-sm btn-danger" onClick={() => {
                    setFormData((prev) => {
                      const d = { ...(prev.decoration_hero_images || { carousel: [], mosaic: [] }) };
                      const arr = [...(d.mosaic || [])].filter((_, j) => j !== i);
                      return { ...prev, decoration_hero_images: { ...d, mosaic: arr } };
                    });
                  }}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>

            <hr className="my-4" />
            <h3 className="h5 mb-2">1. Pillars section — header text</h3>
            <p className="text-muted small mb-3">On the site this sits directly under the hero, above the bento cards.</p>
            <div className="form-group">
              <label>Small label (e.g. &quot;The Aesthetic Pillars&quot;)</label>
              <input type="text" className="form-control" name="section1_title" value={formData.section1_title || ''} onChange={handleChange} placeholder="The Aesthetic Pillars" />
            </div>
            <div className="form-group">
              <label>Main heading (e.g. &quot;Design Elements&quot;)</label>
              <input type="text" className="form-control" name="section1_content" value={formData.section1_content || ''} onChange={handleChange} placeholder="Design Elements" />
            </div>

            <h3 className="h5 mb-2 mt-4">2. Aesthetic pillars — bento cards</h3>
            <p className="text-muted small mb-3">This is what appears on the site under &quot;Design Elements&quot;. Edits here replace the defaults.</p>
            <button
              type="button"
              className="btn btn-sm btn-secondary mb-3"
              onClick={() => setFormData((prev) => ({
                ...prev,
                decoration_pillars: [...(prev.decoration_pillars || []), { id: Date.now(), title: '', description: '', icon: 'Flower2', image: '' }],
              }))}
            >
              + Add pillar
            </button>
            {(formData.decoration_pillars || []).map((p, i) => (
              <div key={p.id || i} className="border rounded p-3 mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <strong>Pillar {i + 1}</strong>
                  <button type="button" className="btn btn-sm btn-danger" onClick={() => setFormData((prev) => ({
                    ...prev,
                    decoration_pillars: (prev.decoration_pillars || []).filter((_, j) => j !== i),
                  }))}
                  >
                    Remove
                  </button>
                </div>
                <div className="form-group">
                  <label>Title</label>
                  <input type="text" className="form-control" value={p.title || ''} onChange={(e) => {
                    const v = e.target.value;
                    setFormData((prev) => {
                      const arr = [...(prev.decoration_pillars || [])];
                      arr[i] = { ...arr[i], title: v };
                      return { ...prev, decoration_pillars: arr };
                    });
                  }}
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea className="form-control" rows={2} value={p.description || ''} onChange={(e) => {
                    const v = e.target.value;
                    setFormData((prev) => {
                      const arr = [...(prev.decoration_pillars || [])];
                      arr[i] = { ...arr[i], description: v };
                      return { ...prev, decoration_pillars: arr };
                    });
                  }}
                  />
                </div>
                <div className="form-group">
                  <label>Icon</label>
                  <select className="form-control" value={p.icon || 'Flower2'} onChange={(e) => {
                    const v = e.target.value;
                    setFormData((prev) => {
                      const arr = [...(prev.decoration_pillars || [])];
                      arr[i] = { ...arr[i], icon: v };
                      return { ...prev, decoration_pillars: arr };
                    });
                  }}
                  >
                    {['Flower2', 'Lightbulb', 'Layers', 'Scissors', 'MapPin', 'Palette', 'Settings'].map((ic) => (
                      <option key={ic} value={ic}>{ic}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Image URL or upload</label>
                  <input type="text" className="form-control mb-2" value={p.image || ''} onChange={(e) => {
                    const v = e.target.value;
                    setFormData((prev) => {
                      const arr = [...(prev.decoration_pillars || [])];
                      arr[i] = { ...arr[i], image: v };
                      return { ...prev, decoration_pillars: arr };
                    });
                  }}
                  placeholder="https://… or /uploads/services/…"
                  />
                  <label className="btn btn-sm btn-outline-primary mb-0">
                    Upload image
                    <input type="file" accept="image/*" className="d-none" onChange={async (e) => {
                      const file = e.target.files?.[0];
                      e.target.value = '';
                      if (!file) return;
                      try {
                        const { url } = await servicesAPI.uploadImage(file);
                        setFormData((prev) => {
                          const arr = [...(prev.decoration_pillars || [])];
                          arr[i] = { ...arr[i], image: url };
                          return { ...prev, decoration_pillars: arr };
                        });
                        showSuccess('Pillar image uploaded');
                      } catch (err) {
                        showError(err.response?.data?.error || 'Upload failed');
                      }
                    }}
                    />
                  </label>
                </div>
                <div className="d-flex gap-1">
                  <button type="button" className="btn btn-sm btn-outline-secondary" disabled={i === 0} onClick={() => setFormData((prev) => {
                    const arr = [...(prev.decoration_pillars || [])];
                    if (i <= 0) return prev;
                    [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]];
                    return { ...prev, decoration_pillars: arr };
                  })}
                  >
                    Move up
                  </button>
                  <button type="button" className="btn btn-sm btn-outline-secondary" disabled={i >= (formData.decoration_pillars || []).length - 1} onClick={() => setFormData((prev) => {
                    const arr = [...(prev.decoration_pillars || [])];
                    if (i >= arr.length - 1) return prev;
                    [arr[i], arr[i + 1]] = [arr[i + 1], arr[i]];
                    return { ...prev, decoration_pillars: arr };
                  })}
                  >
                    Move down
                  </button>
                </div>
              </div>
            ))}

            <hr className="my-4" />
            <h3 className="h5 mb-2">3. Gallery section — header text</h3>
            <p className="text-muted small mb-3">On the site this sits above the masonry grid.</p>
            <div className="form-group">
              <label>Small label</label>
              <input type="text" className="form-control" name="section2_title" value={formData.section2_title || ''} onChange={handleChange} placeholder="From Concept to Reality" />
            </div>
            <div className="form-group">
              <label>Main heading</label>
              <input type="text" className="form-control" name="section2_content" value={formData.section2_content || ''} onChange={handleChange} placeholder="The Gallery" />
            </div>

            <h3 className="h5 mb-2 mt-4">4. Gallery — masonry items</h3>
            <button type="button" className="btn btn-sm btn-secondary mb-2" onClick={() => setFormData((prev) => ({
              ...prev,
              decoration_gallery_projects: [...(prev.decoration_gallery_projects || []), { id: Date.now(), image: '', mood: '', category: '' }],
            }))}
            >
              + Add gallery item
            </button>
            {(formData.decoration_gallery_projects || []).map((g, i) => (
              <div key={g.id || i} className="border rounded p-2 mb-2">
                <div className="d-flex justify-content-between"><strong>Item {i + 1}</strong>
                  <button type="button" className="btn btn-sm btn-danger" onClick={() => setFormData((prev) => ({
                    ...prev,
                    decoration_gallery_projects: (prev.decoration_gallery_projects || []).filter((_, j) => j !== i),
                  }))}
                  >
                    Remove
                  </button>
                </div>
                <input type="text" className="form-control form-control-sm mb-1" placeholder="Mood" value={g.mood || ''} onChange={(e) => {
                  const v = e.target.value;
                  setFormData((prev) => {
                    const arr = [...(prev.decoration_gallery_projects || [])];
                    arr[i] = { ...arr[i], mood: v };
                    return { ...prev, decoration_gallery_projects: arr };
                  });
                }}
                />
                <input type="text" className="form-control form-control-sm mb-1" placeholder="Category" value={g.category || ''} onChange={(e) => {
                  const v = e.target.value;
                  setFormData((prev) => {
                    const arr = [...(prev.decoration_gallery_projects || [])];
                    arr[i] = { ...arr[i], category: v };
                    return { ...prev, decoration_gallery_projects: arr };
                  });
                }}
                />
                <input type="text" className="form-control form-control-sm mb-1" placeholder="Image URL" value={g.image || ''} onChange={(e) => {
                  const v = e.target.value;
                  setFormData((prev) => {
                    const arr = [...(prev.decoration_gallery_projects || [])];
                    arr[i] = { ...arr[i], image: v };
                    return { ...prev, decoration_gallery_projects: arr };
                  });
                }}
                />
                <label className="btn btn-sm btn-outline-primary mb-0">Upload
                  <input type="file" accept="image/*" className="d-none" onChange={async (e) => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    if (!file) return;
                    try {
                      const { url } = await servicesAPI.uploadImage(file);
                      setFormData((prev) => {
                        const arr = [...(prev.decoration_gallery_projects || [])];
                        arr[i] = { ...arr[i], image: url };
                        return { ...prev, decoration_gallery_projects: arr };
                      });
                      showSuccess('Gallery image uploaded');
                    } catch (err) {
                      showError(err.response?.data?.error || 'Upload failed');
                    }
                  }}
                  />
                </label>
              </div>
            ))}

            <hr className="my-4" />
            <h3 className="h5 mb-2">5. Process — timeline</h3>
            <button type="button" className="btn btn-sm btn-secondary mb-2" onClick={() => setFormData((prev) => ({
              ...prev,
              decoration_process_steps: [...(prev.decoration_process_steps || []), { number: String((prev.decoration_process_steps || []).length + 1).padStart(2, '0'), title: '', description: '', icon: 'MapPin' }],
            }))}
            >
              + Add step
            </button>
            {(formData.decoration_process_steps || []).map((s, i) => (
              <div key={i} className="border rounded p-2 mb-2">
                <div className="d-flex justify-content-between"><strong>Step {i + 1}</strong>
                  <button type="button" className="btn btn-sm btn-danger" onClick={() => setFormData((prev) => ({
                    ...prev,
                    decoration_process_steps: (prev.decoration_process_steps || []).filter((_, j) => j !== i),
                  }))}
                  >
                    Remove
                  </button>
                </div>
                <input type="text" className="form-control form-control-sm mb-1" placeholder="Number e.g. 01" value={s.number || ''} onChange={(e) => {
                  const v = e.target.value;
                  setFormData((prev) => {
                    const arr = [...(prev.decoration_process_steps || [])];
                    arr[i] = { ...arr[i], number: v };
                    return { ...prev, decoration_process_steps: arr };
                  });
                }}
                />
                <input type="text" className="form-control form-control-sm mb-1" placeholder="Title" value={s.title || ''} onChange={(e) => {
                  const v = e.target.value;
                  setFormData((prev) => {
                    const arr = [...(prev.decoration_process_steps || [])];
                    arr[i] = { ...arr[i], title: v };
                    return { ...prev, decoration_process_steps: arr };
                  });
                }}
                />
                <textarea className="form-control form-control-sm mb-1" placeholder="Description" rows={2} value={s.description || ''} onChange={(e) => {
                  const v = e.target.value;
                  setFormData((prev) => {
                    const arr = [...(prev.decoration_process_steps || [])];
                    arr[i] = { ...arr[i], description: v };
                    return { ...prev, decoration_process_steps: arr };
                  });
                }}
                />
                <select className="form-control form-control-sm" value={s.icon || 'MapPin'} onChange={(e) => {
                  const v = e.target.value;
                  setFormData((prev) => {
                    const arr = [...(prev.decoration_process_steps || [])];
                    arr[i] = { ...arr[i], icon: v };
                    return { ...prev, decoration_process_steps: arr };
                  });
                }}
                >
                  {['Flower2', 'Lightbulb', 'Layers', 'Scissors', 'MapPin', 'Palette', 'Settings'].map((ic) => (
                    <option key={ic} value={ic}>{ic}</option>
                  ))}
                </select>
              </div>
            ))}

            <hr className="my-4" />
            <h3 className="h5 mb-2">6. Testimonial</h3>
            <div className="form-group">
              <label>Quote</label>
              <textarea className="form-control" rows={3} value={(formData.decoration_testimonial && formData.decoration_testimonial.quote) || ''} onChange={(e) => setFormData((prev) => ({
                ...prev,
                decoration_testimonial: { ...(prev.decoration_testimonial || {}), quote: e.target.value },
              }))}
              />
            </div>
            <div className="form-group">
              <label>Author line</label>
              <input type="text" className="form-control" value={(formData.decoration_testimonial && formData.decoration_testimonial.author) || ''} onChange={(e) => setFormData((prev) => ({
                ...prev,
                decoration_testimonial: { ...(prev.decoration_testimonial || {}), author: e.target.value },
              }))}
              />
            </div>

            <hr className="my-4" />
            <h3 className="h5 mb-2">7. Packages</h3>
            <button type="button" className="btn btn-sm btn-secondary mb-2" onClick={() => setFormData((prev) => ({
              ...prev,
              decoration_packages: [...(prev.decoration_packages || []), { name: '', price: '', features: [] }],
            }))}
            >
              + Add package
            </button>
            {(formData.decoration_packages || []).map((pkg, i) => (
              <div key={i} className="border rounded p-2 mb-2">
                <div className="d-flex justify-content-between"><strong>Package {i + 1}</strong>
                  <button type="button" className="btn btn-sm btn-danger" onClick={() => setFormData((prev) => ({
                    ...prev,
                    decoration_packages: (prev.decoration_packages || []).filter((_, j) => j !== i),
                  }))}
                  >
                    Remove
                  </button>
                </div>
                <input type="text" className="form-control form-control-sm mb-1" placeholder="Name" value={pkg.name || ''} onChange={(e) => {
                  const v = e.target.value;
                  setFormData((prev) => {
                    const arr = [...(prev.decoration_packages || [])];
                    arr[i] = { ...arr[i], name: v };
                    return { ...prev, decoration_packages: arr };
                  });
                }}
                />
                <input type="text" className="form-control form-control-sm mb-1" placeholder="Price" value={pkg.price || ''} onChange={(e) => {
                  const v = e.target.value;
                  setFormData((prev) => {
                    const arr = [...(prev.decoration_packages || [])];
                    arr[i] = { ...arr[i], price: v };
                    return { ...prev, decoration_packages: arr };
                  });
                }}
                />
                <label className="small text-muted">Features (one per line)</label>
                <textarea className="form-control form-control-sm" rows={4} value={Array.isArray(pkg.features) ? pkg.features.join('\n') : ''} onChange={(e) => {
                  const lines = e.target.value.split('\n').map((l) => l.trim()).filter(Boolean);
                  setFormData((prev) => {
                    const arr = [...(prev.decoration_packages || [])];
                    arr[i] = { ...arr[i], features: lines };
                    return { ...prev, decoration_packages: arr };
                  });
                }}
                />
              </div>
            ))}

            <hr className="my-4" />
            <h3 className="h5 mb-2">8. Bottom CTA</h3>
            <p className="text-muted small mb-3">Last section before the footer on the public page.</p>
            <div className="form-group">
              <label>Title</label>
              <input type="text" className="form-control" name="cta_title" value={formData.cta_title || ''} onChange={handleChange} placeholder="Ready to Transform Your Space?" />
            </div>
          </div>
        )}
        {isVideoEditingService && (
          <div className="form-section">
            <h2>Video Editing Page Content</h2>
            <p className="text-muted small mb-3">
              Controls the public <code>/video-editing</code> page (hero copy, section labels, portfolio grid, workflow, tools). Use <code>\n</code> in the headline for line breaks.
            </p>

            <h3 className="h5 mb-2">Hero</h3>
            <div className="form-group">
              <label>Headline (shown first; falls back to generic Hero Title if empty)</label>
              <input
                type="text"
                className="form-control"
                value={formData.video_editing_hero_title || ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, video_editing_hero_title: e.target.value }))}
                placeholder={'Video Editing\\n& Post-Production'}
              />
            </div>
            <div className="form-group">
              <label>Optional hero background video</label>
              <div className="service-form-upload-row mb-1">
                <input
                  type="text"
                  className="form-control"
                  name="video_editing_hero_video"
                  value={formData.video_editing_hero_video ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFormData((prev) => ({ ...prev, video_editing_hero_video: v }));
                  }}
                  placeholder="/uploads/services/… or https://…"
                />
                <label className="btn btn-outline-secondary btn-sm mb-0 text-nowrap">
                  Upload video
                  <input
                    type="file"
                    accept="video/*"
                    className="d-none service-form-file-input-hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      e.target.value = '';
                      if (!file) return;
                      try {
                        const res = await servicesAPI.uploadVideo(file);
                        const url = typeof res?.url === 'string' ? res.url.trim() : '';
                        if (!url) {
                          showError('Upload succeeded but no file URL was returned.');
                          return;
                        }
                        setFormData((prev) => ({ ...prev, video_editing_hero_video: url }));
                        showSuccess('Hero video uploaded — click Update Service below to save.');
                      } catch (err) {
                        showError(err.response?.data?.error || err.message || 'Upload failed');
                      }
                    }}
                  />
                </label>
              </div>
              <small className="form-text text-muted">
                Plays behind the headline. Paste a URL/path or upload (up to ~500MB per file on the server). Upload stores the file on the server; you must still click <strong>Update Service</strong> so the path is saved to the database and shown on the public site.
              </small>
              {String(formData.video_editing_hero_video || '').trim() && (
                <div className="service-form-video-preview-wrap mt-2">
                  <span className="small text-muted d-block mb-1">Hero preview</span>
                  <video
                    className="service-form-video-preview"
                    key={`hero-${formData.video_editing_hero_video}`}
                    src={resolveVideoEditingMediaUrl(formData.video_editing_hero_video)}
                    controls
                    muted
                    playsInline
                    preload="metadata"
                  />
                </div>
              )}
            </div>
            <div className="form-group">
              <label>Badge above headline</label>
              <input
                type="text"
                className="form-control"
                name="hero_subtitle"
                value={formData.hero_subtitle || ''}
                onChange={handleChange}
                placeholder="Its_Kasim Production // Post-Production"
              />
            </div>

            <hr className="my-4" />
            <h3 className="h5 mb-2">Portfolio section (labels)</h3>
            <div className="form-group">
              <label>Small label (badge)</label>
              <input type="text" className="form-control" name="section1_title" value={formData.section1_title || ''} onChange={handleChange} placeholder="Portfolio" />
            </div>
            <div className="form-group">
              <label>Main heading</label>
              <input type="text" className="form-control" name="section1_content" value={formData.section1_content || ''} onChange={handleChange} placeholder="Selected Works" />
            </div>

            <div className="form-group">
              <label>Portfolio items (bento grid)</label>
              <p className="text-muted small mb-2">
                <strong>Idle clip</strong> is the MP4 shown in the grid when the card is not hovered (and on touch devices until someone taps).{' '}
                <strong>Hover clip</strong> plays on desktop when the visitor hovers the card. Use the same file for both if you prefer.{' '}
                On the public site, <strong>clicking</strong> a card opens a full player using the hover clip, or the idle clip if hover is empty.
              </p>
              <button
                type="button"
                className="btn btn-sm btn-secondary mb-2"
                onClick={() => setFormData((prev) => ({
                  ...prev,
                  video_editing_portfolio: [...(prev.video_editing_portfolio || []), { id: Date.now(), title: '', role: '', year: '', thumbnail: '', preview: '' }],
                }))}
              >
                + Add project
              </button>
              {(formData.video_editing_portfolio || []).map((p, i) => (
                <div key={p.id || i} className="border rounded p-2 mb-2 service-form-video-project-block">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong>Project {i + 1}</strong>
                    <button type="button" className="btn btn-sm btn-danger" onClick={() => setFormData((prev) => ({
                      ...prev,
                      video_editing_portfolio: (prev.video_editing_portfolio || []).filter((_, j) => j !== i),
                    }))}
                    >
                      Remove
                    </button>
                  </div>
                  <input type="text" className="form-control form-control-sm mb-1" placeholder="Title" value={p.title || ''} onChange={(e) => {
                    const v = e.target.value;
                    setFormData((prev) => {
                      const arr = [...(prev.video_editing_portfolio || [])];
                      arr[i] = { ...arr[i], title: v };
                      return { ...prev, video_editing_portfolio: arr };
                    });
                  }}
                  />
                  <input type="text" className="form-control form-control-sm mb-1" placeholder="Role" value={p.role || ''} onChange={(e) => {
                    const v = e.target.value;
                    setFormData((prev) => {
                      const arr = [...(prev.video_editing_portfolio || [])];
                      arr[i] = { ...arr[i], role: v };
                      return { ...prev, video_editing_portfolio: arr };
                    });
                  }}
                  />
                  <input type="text" className="form-control form-control-sm mb-1" placeholder="Year" value={p.year || ''} onChange={(e) => {
                    const v = e.target.value;
                    setFormData((prev) => {
                      const arr = [...(prev.video_editing_portfolio || [])];
                      arr[i] = { ...arr[i], year: v };
                      return { ...prev, video_editing_portfolio: arr };
                    });
                  }}
                  />
                  <label className="small text-muted mb-0">Idle grid clip (MP4)</label>
                  <div className="service-form-upload-row mb-2">
                    <input type="text" className="form-control form-control-sm" placeholder="Path or URL, e.g. /uploads/services/…" value={p.thumbnail || ''} onChange={(e) => {
                      const v = e.target.value;
                      setFormData((prev) => {
                        const arr = [...(prev.video_editing_portfolio || [])];
                        arr[i] = { ...arr[i], thumbnail: v };
                        return { ...prev, video_editing_portfolio: arr };
                      });
                    }}
                    />
                    <label className="btn btn-outline-secondary btn-sm mb-0 text-nowrap">
                      Upload
                      <input
                        type="file"
                        accept="video/*"
                        className="d-none service-form-file-input-hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          e.target.value = '';
                          if (!file) return;
                          try {
                            const { url } = await servicesAPI.uploadVideo(file);
                            setFormData((prev) => {
                              const arr = [...(prev.video_editing_portfolio || [])];
                              arr[i] = { ...arr[i], thumbnail: url };
                              return { ...prev, video_editing_portfolio: arr };
                            });
                            showSuccess('Idle clip uploaded');
                          } catch (err) {
                            showError(err.response?.data?.error || err.message || 'Upload failed');
                          }
                        }}
                      />
                    </label>
                  </div>
                  {p.thumbnail && String(p.thumbnail).trim() && (
                    <div className="service-form-video-preview-wrap">
                      <span className="small text-muted d-block mb-1">Idle clip preview</span>
                      <video
                        className="service-form-video-preview"
                        key={`idle-${i}-${p.thumbnail}`}
                        src={resolveVideoEditingMediaUrl(p.thumbnail)}
                        controls
                        muted
                        playsInline
                        preload="metadata"
                      />
                    </div>
                  )}
                  <label className="small text-muted mb-0">Hover clip (MP4, optional)</label>
                  <div className="service-form-upload-row">
                    <input type="text" className="form-control form-control-sm" placeholder="Same as idle or a different file" value={p.preview || ''} onChange={(e) => {
                      const v = e.target.value;
                      setFormData((prev) => {
                        const arr = [...(prev.video_editing_portfolio || [])];
                        arr[i] = { ...arr[i], preview: v };
                        return { ...prev, video_editing_portfolio: arr };
                      });
                    }}
                    />
                    <label className="btn btn-outline-secondary btn-sm mb-0 text-nowrap">
                      Upload
                      <input
                        type="file"
                        accept="video/*"
                        className="d-none service-form-file-input-hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          e.target.value = '';
                          if (!file) return;
                          try {
                            const { url } = await servicesAPI.uploadVideo(file);
                            setFormData((prev) => {
                              const arr = [...(prev.video_editing_portfolio || [])];
                              arr[i] = { ...arr[i], preview: url };
                              return { ...prev, video_editing_portfolio: arr };
                            });
                            showSuccess('Hover clip uploaded');
                          } catch (err) {
                            showError(err.response?.data?.error || err.message || 'Upload failed');
                          }
                        }}
                      />
                    </label>
                  </div>
                  {p.preview && String(p.preview).trim() && (
                    <div className="service-form-video-preview-wrap mt-2">
                      <span className="small text-muted d-block mb-1">Hover clip preview</span>
                      <video
                        className="service-form-video-preview"
                        key={`hover-${i}-${p.preview}`}
                        src={resolveVideoEditingMediaUrl(p.preview)}
                        controls
                        muted
                        playsInline
                        preload="metadata"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>

            <hr className="my-4" />
            <h3 className="h5 mb-2">Workflow section (labels)</h3>
            <div className="form-group">
              <label>Small label (badge)</label>
              <input type="text" className="form-control" name="section2_title" value={formData.section2_title || ''} onChange={handleChange} placeholder="Workflow" />
            </div>
            <div className="form-group">
              <label>Main heading</label>
              <input type="text" className="form-control" name="section2_content" value={formData.section2_content || ''} onChange={handleChange} placeholder="Services & Process" />
            </div>

            <div className="form-group">
              <label>Workflow steps</label>
              <button
                type="button"
                className="btn btn-sm btn-secondary mb-2"
                onClick={() => setFormData((prev) => ({
                  ...prev,
                  video_editing_workflow: [...(prev.video_editing_workflow || []), { icon: 'Film', title: '', description: '' }],
                }))}
              >
                + Add step
              </button>
              {(formData.video_editing_workflow || []).map((s, i) => (
                <div key={i} className="border rounded p-2 mb-2">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong>Step {i + 1}</strong>
                    <button type="button" className="btn btn-sm btn-danger" onClick={() => setFormData((prev) => ({
                      ...prev,
                      video_editing_workflow: (prev.video_editing_workflow || []).filter((_, j) => j !== i),
                    }))}
                    >
                      Remove
                    </button>
                  </div>
                  <select className="form-control form-control-sm mb-1" value={s.icon || 'Film'} onChange={(e) => {
                    const v = e.target.value;
                    setFormData((prev) => {
                      const arr = [...(prev.video_editing_workflow || [])];
                      arr[i] = { ...arr[i], icon: v };
                      return { ...prev, video_editing_workflow: arr };
                    });
                  }}
                  >
                    {['Film', 'Palette', 'Music', 'Zap'].map((ic) => (
                      <option key={ic} value={ic}>{ic}</option>
                    ))}
                  </select>
                  <input type="text" className="form-control form-control-sm mb-1" placeholder="Title" value={s.title || ''} onChange={(e) => {
                    const v = e.target.value;
                    setFormData((prev) => {
                      const arr = [...(prev.video_editing_workflow || [])];
                      arr[i] = { ...arr[i], title: v };
                      return { ...prev, video_editing_workflow: arr };
                    });
                  }}
                  />
                  <textarea className="form-control form-control-sm" rows={2} placeholder="Description" value={s.description || ''} onChange={(e) => {
                    const v = e.target.value;
                    setFormData((prev) => {
                      const arr = [...(prev.video_editing_workflow || [])];
                      arr[i] = { ...arr[i], description: v };
                      return { ...prev, video_editing_workflow: arr };
                    });
                  }}
                  />
                </div>
              ))}
            </div>

            <hr className="my-4" />
            <h3 className="h5 mb-2">Tools &amp; CTA</h3>
            <div className="form-group">
              <label>Software tools (marquee)</label>
              <p className="text-muted small mb-2">
                Icon can be an emoji, a direct image URL, or a file from this server. Flaticon/Freepik usually copy HTML or a picture — use <strong>Paste</strong> in the icon field after copying from the site, or <strong>Upload</strong>, or paste a <strong>direct image link</strong> (right‑click image → copy image address).
              </p>
              <button
                type="button"
                className="btn btn-sm btn-secondary mb-2"
                onClick={() => setFormData((prev) => ({
                  ...prev,
                  video_editing_tools: [...(prev.video_editing_tools || []), { name: '', icon: '🎬' }],
                }))}
              >
                + Add tool
              </button>
              {(formData.video_editing_tools || []).map((t, i) => (
                <div key={i} className="border rounded p-2 mb-2 service-form-video-project-block">
                  <div className="d-flex gap-2 mb-2 align-items-center">
                    <input type="text" className="form-control form-control-sm flex-grow-1" placeholder="Name" value={t.name || ''} onChange={(e) => {
                      const v = e.target.value;
                      setFormData((prev) => {
                        const arr = [...(prev.video_editing_tools || [])];
                        arr[i] = { ...arr[i], name: v };
                        return { ...prev, video_editing_tools: arr };
                      });
                    }}
                    />
                    <button type="button" className="btn btn-sm btn-outline-danger flex-shrink-0" onClick={() => setFormData((prev) => ({
                      ...prev,
                      video_editing_tools: (prev.video_editing_tools || []).filter((_, j) => j !== i),
                    }))}
                    >
                      ×
                    </button>
                  </div>
                  <label className="small text-muted mb-0">Icon (emoji, URL, paste image, or upload)</label>
                  <div className="service-form-upload-row mb-1 align-items-center">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="🎬 or https://… or /uploads/services/…"
                      value={t.icon || ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        setFormData((prev) => {
                          const arr = [...(prev.video_editing_tools || [])];
                          arr[i] = { ...arr[i], icon: v };
                          return { ...prev, video_editing_tools: arr };
                        });
                      }}
                      onPaste={(e) => handleVideoEditingToolIconPaste(e, i, setFormData)}
                    />
                    <label className="btn btn-outline-secondary btn-sm mb-0 text-nowrap">
                      Upload
                      <input
                        type="file"
                        accept="image/*,.svg"
                        className="d-none service-form-file-input-hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          e.target.value = '';
                          if (!file) return;
                          try {
                            const { url } = await servicesAPI.uploadImage(file);
                            setFormData((prev) => {
                              const arr = [...(prev.video_editing_tools || [])];
                              arr[i] = { ...arr[i], icon: url };
                              return { ...prev, video_editing_tools: arr };
                            });
                            showSuccess('Icon uploaded');
                          } catch (err) {
                            showError(err.response?.data?.error || err.message || 'Upload failed');
                          }
                        }}
                      />
                    </label>
                  </div>
                  {toolIconPreviewSrc(t.icon) ? (
                    <div className="d-flex align-items-center gap-2 mt-1">
                      <span className="small text-muted">Preview:</span>
                      <img src={toolIconPreviewSrc(t.icon)} alt="" className="rounded" style={{ width: 36, height: 36, objectFit: 'contain', background: 'rgba(0,0,0,0.06)' }} />
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
            <div className="form-group">
              <label>Bottom CTA title</label>
              <input type="text" className="form-control" name="cta_title" value={formData.cta_title || ''} onChange={handleChange} placeholder="Let&apos;s tell your story." />
            </div>
            <div className="form-group">
              <label>CTA subtitle (optional)</label>
              <textarea className="form-control" name="cta_subtitle" value={formData.cta_subtitle || ''} onChange={handleChange} rows={2} placeholder="" />
            </div>
          </div>
        )}
        {(formData.slug === 'photo-editing' || formData.route === '/photo-editing') && (
          <div className="form-section">
            <h2>Photo Editing Page Content</h2>
            <label>Hero Title</label>
            <input type="text" className="form-control mb-2" value={formData.photo_editing_hero_title || ''} onChange={(e) => setFormData(prev => ({ ...prev, photo_editing_hero_title: e.target.value }))} placeholder="THE ART OF THE EDIT" />
          </div>
        )}
        {(formData.slug === 'djing' || formData.route === '/djing') && (
          <div className="form-section">
            <h2>DJing Page Content</h2>
            <div className="form-group">
              <label>Hero Title (use \n for line breaks)</label>
              <input type="text" className="form-control" value={formData.djing_hero_title || ''} onChange={(e) => setFormData(prev => ({ ...prev, djing_hero_title: e.target.value }))} placeholder="THE BEAT BEHIND THE MOMENT" />
            </div>
            <div className="form-group">
              <label>Audio Track Title</label>
              <input type="text" className="form-control" value={formData.djing_audio_title || ''} onChange={(e) => setFormData(prev => ({ ...prev, djing_audio_title: e.target.value }))} placeholder="Summer Vibes Mix 2024" />
            </div>
          </div>
        )}

        {/* GRAPHICS DESIGN SPECIFIC SECTION */}
        {(formData.slug === 'graphics-design' || formData.route === '/graphics-design') && (
        <div className="form-section graphics-section">
            <h2>Graphics Design Layout</h2>
            <p className="text-muted">
              These settings control the portfolio grid, Work-in-Progress section and Typography section
              on the Graphics Design page.
            </p>

            {/* Categories */}
            <div className="form-group">
              <label>Portfolio Categories (order matches the buttons on the page)</label>
              {(formData.graphics_portfolio_categories || []).map((cat, index) => (
                <div key={index} className="graphics-category-item">
                  <span className="graphics-badge">#{index}</span>
                  <input
                    type="text"
                    className="form-control"
                    value={cat}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData(prev => {
                        const next = [...(prev.graphics_portfolio_categories || [])];
                        next[index] = value;
                        return { ...prev, graphics_portfolio_categories: next };
                      });
                    }}
                    placeholder={index === 0 ? 'All Work' : ''}
                  />
                </div>
              ))}
            </div>

            {/* Portfolio Items */}
            <div className="form-group">
              <label>Portfolio Items (Gallery Grid)</label>
              <small className="form-text text-muted d-block mb-2">
                These are the images displayed in the main portfolio grid. Each item needs an image, title, category, and type.
              </small>
              <button
                type="button"
                onClick={addPortfolioItem}
                className="btn btn-sm btn-secondary mb-3"
              >
                + Add Portfolio Item
              </button>
              
              {(formData.graphics_portfolio_items || []).map((item, index) => {
                const itemImage = images.graphics_portfolio_items?.[index];
                const itemPreview = imagePreviews.graphics_portfolio_items?.[index] || 
                  (item.image ? `${getApiBaseUrl()}${item.image.startsWith('/') ? '' : '/'}${item.image}` : null);
                
                return (
                  <div key={item.id || index} className="portfolio-item-card mb-3 p-3 border rounded">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <strong>Item #{index + 1}</strong>
                      <button
                        type="button"
                        onClick={() => removePortfolioItem(index)}
                        className="btn btn-sm btn-danger"
                      >
                        Remove
                      </button>
                    </div>
                    
                    <div className="row">
                      <div className="col-md-6 mb-2">
                        <label>Image</label>
                        <input
                          type="file"
                          className="form-control"
                          accept="image/*"
                          onChange={(e) => handlePortfolioItemImageChange(index, e)}
                        />
                        {itemPreview && (
                          <div className="mt-2">
                            <ImagePreview
                              preview={itemPreview}
                              label={`Portfolio Item ${index + 1}`}
                              onRemove={() => {
                                setImages(prev => {
                                  const newImages = [...(prev.graphics_portfolio_items || [])];
                                  newImages[index] = null;
                                  return { ...prev, graphics_portfolio_items: newImages };
                                });
                                setImagePreviews(prev => {
                                  const newPreviews = [...(prev.graphics_portfolio_items || [])];
                                  if (newPreviews[index] && newPreviews[index].startsWith('blob:')) {
                                    URL.revokeObjectURL(newPreviews[index]);
                                  }
                                  newPreviews[index] = null;
                                  return { ...prev, graphics_portfolio_items: newPreviews };
                                });
                                updatePortfolioItem(index, 'image', '');
                              }}
                              fileName={itemImage?.name}
                              isNew={!!itemImage}
                            />
                          </div>
                        )}
                      </div>
                      
                      <div className="col-md-6 mb-2">
                        <label>Title</label>
                        <input
                          type="text"
                          className="form-control"
                          value={item.title || ''}
                          onChange={(e) => updatePortfolioItem(index, 'title', e.target.value)}
                          placeholder="e.g., Social Media Influencer Poster"
                        />
                      </div>
                      
                      <div className="col-md-4 mb-2">
                        <label>Category</label>
                        <select
                          className="form-control"
                          value={normalizeCategoryToSlug(item.category) || 'branding'}
                          onChange={(e) => updatePortfolioItem(index, 'category', e.target.value)}
                        >
                          {(formData.graphics_portfolio_categories || []).slice(1).map((cat, idx) => {
                            const slug = normalizeCategoryToSlug(cat);
                            if (slug === 'all') return null;
                            const label = typeof cat === 'string' ? cat : cat?.name || cat;
                            return (
                              <option key={`${slug}-${idx}`} value={slug}>
                                {label}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                      
                      <div className="col-md-4 mb-2">
                        <label>Type</label>
                        <select
                          className="form-control"
                          value={item.type || 'final'}
                          onChange={(e) => updatePortfolioItem(index, 'type', e.target.value)}
                        >
                          <option value="final">Final</option>
                          <option value="sketch">Sketch</option>
                        </select>
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {(!formData.graphics_portfolio_items || formData.graphics_portfolio_items.length === 0) && (
                <p className="text-muted">No portfolio items yet. Click "Add Portfolio Item" to get started.</p>
              )}
            </div>

            {/* Work in Progress */}
            <div className="form-group">
              <label>Work-in-Progress Section</label>
              <div className="form-check mb-2">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="graphics_wip_enabled"
                  name="graphics_wip_enabled"
                  checked={!!formData.graphics_wip_enabled}
                  onChange={handleChange}
                />
                <label className="form-check-label" htmlFor="graphics_wip_enabled">
                  Enable Work-in-Progress section
                </label>
              </div>

              {formData.graphics_wip_enabled && (
                <div className="graphics-wip-grid">
                  <div className="graphics-wip-column">
                    <label>Sketch Image (left)</label>
                    <input
                      type="file"
                      className="form-control"
                      name="graphics_wip_sketch_image"
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                    <ImagePreview
                      preview={imagePreviews.graphics_wip_sketch_image}
                      label="Sketch"
                      onRemove={() => handleRemoveImage('graphics_wip_sketch_image')}
                      fileName={images.graphics_wip_sketch_image?.name}
                      isNew={!!images.graphics_wip_sketch_image}
                    />
                  </div>
                  <div className="graphics-wip-column">
                    <label>Final Image (right)</label>
                    <input
                      type="file"
                      className="form-control"
                      name="graphics_wip_final_image"
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                    <ImagePreview
                      preview={imagePreviews.graphics_wip_final_image}
                      label="Final"
                      onRemove={() => handleRemoveImage('graphics_wip_final_image')}
                      fileName={images.graphics_wip_final_image?.name}
                      isNew={!!images.graphics_wip_final_image}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Typography */}
            <div className="form-group">
              <label>Typography Letters</label>
              <small className="form-text">
                Controls the big A/B/C/D blocks under the TYPOGRAPHY section.
              </small>
              <div className="graphics-typography-grid">
                {(formData.graphics_typography_showcase || []).map((item, index) => (
                  <div key={index} className="graphics-typography-item">
                    <input
                      type="text"
                      maxLength={1}
                      className="form-control mb-2"
                      value={item.text || ''}
                      onChange={(e) => {
                        const value = e.target.value.toUpperCase().slice(0, 1);
                        setFormData(prev => {
                          const next = [...(prev.graphics_typography_showcase || [])];
                          next[index] = { ...(next[index] || {}), text: value };
                          return { ...prev, graphics_typography_showcase: next };
                        });
                      }}
                      placeholder="A"
                    />
                    <select
                      className="form-control"
                      value={item.size || 'medium'}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData(prev => {
                          const next = [...(prev.graphics_typography_showcase || [])];
                          next[index] = { ...(next[index] || {}), size: value };
                          return { ...prev, graphics_typography_showcase: next };
                        });
                      }}
                    >
                      <option value="small">Small</option>
                      <option value="medium">Medium</option>
                      <option value="large">Large</option>
                    </select>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* WHY CHOOSE US — not used on Event Decoration (that page uses “Aesthetic pillars” in Event Decoration block) */}
        {!isGraphicsDesignService && !isWebDesignService && !isEventDecorationService && !isVideoEditingService && (
          <div className="form-section">
            <h2>Why Choose Us Features</h2>

          {formData.why_choose_us.map((feature, index) => (
            <div key={index} className="feature-item">
              <h4>Feature {index + 1}</h4>
              <div className="form-group">
                <label>Feature Title</label>
                <input
                  type="text"
                  className="form-control"
                  value={feature.title}
                  onChange={(e) => handleWhyChooseUsChange(index, 'title', e.target.value)}
                  placeholder="FASHION EXPERTISE"
                />
              </div>
              <div className="form-group">
                <label>Feature Description</label>
                <textarea
                  className="form-control"
                  value={feature.description}
                  onChange={(e) => handleWhyChooseUsChange(index, 'description', e.target.value)}
                  rows="2"
                  placeholder="We have a keen eye for fashion and style..."
                />
              </div>
              {formData.why_choose_us.length > 1 && (
                <button 
                  type="button" 
                  onClick={() => removeWhyChooseUsItem(index)}
                  className="btn btn-sm btn-danger"
                >
                  Remove Feature
                </button>
              )}
            </div>
          ))}

          <button 
            type="button" 
            onClick={addWhyChooseUsItem}
            className="btn btn-secondary"
          >
            + Add Feature
          </button>
          </div>
        )}

        {/* WEDDING FASHION PHOTOGRAPHY - Photograph service only (3 blocks, each with title + content + image) */}
        {(formData.slug === 'photograph' || formData.name?.toLowerCase().includes('photograph')) && (
          <div className="form-section">
            <h2>Wedding Fashion Photography</h2>
            <p className="form-text text-muted mb-3">
              Three separate blocks, each with its own title, content, and image. Each block appears as its own section on the Photograph page.
            </p>
            {[0, 1, 2].map((blockIndex) => {
              const weddingItems = (formData.custom_sections || []).filter(s => s.type === 'wedding_fashion');
              const item = weddingItems[blockIndex] || {
                type: 'wedding_fashion', title: blockIndex === 0 ? 'WEDDING FASHION PHOTOGRAPHY' : '', content: '', images: ['']
              };
              const imagePath = (item.images && item.images[0]) || '';
              const previewUrl = imagePath ? (imagePath.startsWith('http') ? imagePath : `${getApiBaseUrl()}${imagePath.startsWith('/') ? '' : '/'}${imagePath}`) : null;
              const getWeddingItems = () => (formData.custom_sections || []).filter(s => s.type === 'wedding_fashion');
              const setWeddingItem = (idx, updated) => {
                const items = [...getWeddingItems()];
                while (items.length <= idx) items.push({ type: 'wedding_fashion', title: '', content: '', images: [''] });
                items[idx] = updated;
                const others = (formData.custom_sections || []).filter(s => s.type !== 'wedding_fashion');
                setFormData(prev => ({ ...prev, custom_sections: [...items, ...others] }));
              };
              return (
                <div key={blockIndex} className="nested-item-card mb-4" style={{ padding: '1rem', border: '1px solid #dee2e6', borderRadius: '6px' }}>
                  <h4 className="mb-3">Block {blockIndex + 1}</h4>
                  <div className="form-group">
                    <label>Section Title</label>
                    <input
                      type="text"
                      className="form-control"
                      value={item.title || ''}
                      onChange={(e) => setWeddingItem(blockIndex, { ...item, title: e.target.value })}
                      placeholder={blockIndex === 0 ? 'WEDDING FASHION PHOTOGRAPHY' : 'Section title'}
                    />
                  </div>
                  <div className="form-group">
                    <label>Section Content</label>
                    <textarea
                      className="form-control form-control-content"
                      rows="2"
                      value={item.content || ''}
                      onChange={(e) => setWeddingItem(blockIndex, { ...item, content: e.target.value })}
                      placeholder={blockIndex === 0 ? "Don't forget the groom! We focus on capturing..." : 'Description for this block'}
                    />
                  </div>
                  <div className="form-group">
                    <label>Image</label>
                    <div className="d-flex align-items-start gap-2 flex-wrap">
                      <input
                        type="file"
                        id={`wedding_fashion_image_${blockIndex}`}
                        className="file-input-hidden"
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target?.files?.[0];
                          if (!file) return;
                          try {
                            const { url } = await servicesAPI.uploadImage(file);
                            setWeddingItem(blockIndex, { ...item, images: [url] });
                            showSuccess(`Block ${blockIndex + 1} image uploaded`);
                          } catch (err) {
                            showError(err.response?.data?.error || 'Upload failed');
                          }
                          e.target.value = '';
                        }}
                      />
                      <label htmlFor={`wedding_fashion_image_${blockIndex}`} className="file-upload-label" style={{ marginBottom: 0 }}>
                        Choose Image
                      </label>
                      {previewUrl && (
                        <div className="image-preview-container existing-image-preview">
                          <div className="image-preview-wrapper">
                            <img src={previewUrl} alt={`Block ${blockIndex + 1}`} className="image-preview-img" />
                            <button
                              type="button"
                              onClick={() => setWeddingItem(blockIndex, { ...item, images: [''] })}
                              className="remove-image-btn"
                              title="Remove image"
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* OPTIONS */}
        {isVideographService && (
          <div className="form-section">
            <h2>Featured Films</h2>
            <p className="text-muted">Upload videos/images shown in the public "Featured Films" grid.</p>

            <div className="form-group">
              <label htmlFor="featured_films_media">Featured Films Media</label>
              <input
                type="file"
                className="form-control"
                id="featured_films_media"
                name="gallery_images"
                onChange={handleImageChange}
                accept="video/*,image/*"
                multiple
              />
              <small className="form-text">You can select multiple files and click Update Service to save.</small>

              {imagePreviews.gallery_images && imagePreviews.gallery_images.length > 0 && (
                <div className="gallery-previews-grid">
                  {imagePreviews.gallery_images.map((previewUrl, index) => {
                    const isVideo = typeof previewUrl === 'string' && (
                      previewUrl.includes('.mp4') ||
                      previewUrl.includes('.webm') ||
                      previewUrl.includes('.mov') ||
                      previewUrl.includes('.avi') ||
                      previewUrl.startsWith('blob:')
                    );
                    return (
                      <div key={`${previewUrl}-${index}`} className="gallery-preview-item">
                        {isVideo ? (
                          <video src={previewUrl} controls preload="metadata" />
                        ) : (
                          <img src={previewUrl} alt={`Featured film ${index + 1}`} />
                        )}
                        <button
                          type="button"
                          className="remove-gallery-btn"
                          onClick={() => handleRemoveGalleryItem(index)}
                          title="Remove media"
                        >
                          ✕
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="form-section">
          <h2>Options</h2>

          <div className="form-group">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="booking_enabled"
                name="booking_enabled"
                checked={formData.booking_enabled}
                onChange={handleChange}
              />
              <label className="form-check-label" htmlFor="booking_enabled">
                Enable Booking Form on Service Page
              </label>
            </div>
          </div>

          <div className="form-group">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="is_active"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
              />
              <label className="form-check-label" htmlFor="is_active">
                Active (visible on website)
              </label>
            </div>
          </div>
        </div>

        {/* SUBMIT */}
        <div className="form-actions">
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Saving...' : (isEditing ? 'Update Service' : 'Create Service')}
          </button>
          <button 
            type="button" 
            onClick={() => navigate('/services')}
            className="btn btn-secondary"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default ServiceForm;
